/**
 * E2EE facade for DMs, backed by @getmaapp/signal-wasm.
 *
 * Public API:
 *   const c = new E2eeCrypto();                // one per logged-in tab
 *   await c.initIdentity();                    // idempotent; generates + publishes bundle
 *   const fanout = await c.encryptMultiDevice( // sends to recipient (+ own siblings)
 *     recipientAccountId, { text, mediaKey, mediaIv });
 *   const pt = await c.decryptMessage(         // receives from sender
 *     senderAccountId, ciphertextBase64, ciphertextType, senderDeviceId);
 *   const { bytes, key, iv } = await c.encryptBytes(plainBytes);
 *   const plain = await c.decryptBytes(encBytes, key, iv);
 *
 * (L-7: the legacy single-device encryptMessage — which hardcoded device 1 and
 * silently dropped multi-device recipients — was removed; use
 * encryptMultiDevice.)
 *
 * Sessions are negotiated lazily on first outbound message; inbound
 * PreKeySignalMessages are handled transparently by the WASM client.
 */

import init, {
  initSync as signalInitSync,
  SignalClient,
  WasmPreKey,
  WasmKyberPreKey,
} from '@getmaapp/signal-wasm';

import { SignalProtocolStore } from './signal_store';
import type { SiblingBackfillEntry } from './signal_store';
import { E2eeError, wrapWasm } from './wasm_errors';
export { E2eeError } from './wasm_errors';

// ---------- helpers ----------
function b64Encode(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < u8.length; i += 0x8000) {
    s += String.fromCharCode.apply(
      null,
      u8.subarray(i, i + 0x8000) as unknown as number[],
    );
  }
  return btoa(s);
}

function b64Decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function utf8Encode(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function utf8Decode(b: Uint8Array): string {
  return new TextDecoder().decode(b);
}

// Parse a single varint field from Signal protobuf bytes.
// Signal messages: byte[0] = version, byte[1]+ = protobuf fields (TLV).
// Both SenderKeyDistributionMessage and SenderKeyMessage share the same layout:
//   field1=distributionUuid(bytes,16)  field2=chainId(varint,random)  field3=iteration(varint,0-based)
// Use field3 to read the chain iteration for BOTH skdm bytes and group message ciphertext.
// Returns the value of the FIRST occurrence of targetField with wire type 0, or null.
export function parseSignalProtobufVarint(
  bytes: Uint8Array,
  targetField: number,
): bigint | null {
  try {
    let pos = 1; // skip version byte
    while (pos < bytes.length) {
      let tag = 0n,
        shift = 0n,
        b = 0;
      do {
        b = bytes[pos++]!;
        tag |= BigInt(b & 0x7f) << shift;
        shift += 7n;
      } while (b & 0x80 && pos < bytes.length);
      const fn = Number(tag >> 3n),
        wt = Number(tag & 7n);
      if (wt === 0) {
        let val = 0n;
        shift = 0n;
        do {
          b = bytes[pos++]!;
          val |= BigInt(b & 0x7f) << shift;
          shift += 7n;
        } while (b & 0x80 && pos < bytes.length);
        if (fn === targetField) return val;
      } else if (wt === 2) {
        let len = 0n;
        shift = 0n;
        do {
          b = bytes[pos++]!;
          len |= BigInt(b & 0x7f) << shift;
          shift += 7n;
        } while (b & 0x80 && pos < bytes.length);
        pos += Number(len);
      } else break; // unknown wire type — stop
    }
  } catch {
    /* malformed bytes */
  }
  return null;
}

// Narrow a parsed protobuf varint to a JS number when it fits safely.
function bigintToSafeNumber(v: bigint | null): number | undefined {
  if (v === null || v < 0n) return undefined;
  return v <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(v) : undefined;
}

// Byte-wise equality for SKDM content comparison (M-6).
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// M-7 / L-8: best-effort discriminator between PreKeySignalMessage (3) and
// WhisperMessage (2) from the ciphertext's own protobuf layout, used to
// DISTRUST the server-stored ciphertext_type when the two contradict.
// Verified against the vendored @getmaapp/signal-wasm (2026-07): a
// PreKeySignalMessage leads with field 1 as a VARINT (pre_key_id) or omits
// field 1 entirely (last-resort establishment without an OTK — its first
// field is then field 2, BYTES base_key); a WhisperMessage always leads with
// field 1 as BYTES (the 33-byte ratchet key). Returns null when the layout is
// ambiguous/unparseable — callers must then fall back to the stored type.
function sniffSignalMessageType(ct: Uint8Array): 2 | 3 | null {
  try {
    if (ct.length < 2 || ct[0]! >> 4 < 3) return null; // version byte
    let pos = 1;
    let tag = 0n,
      shift = 0n,
      b = 0;
    do {
      b = ct[pos++]!;
      tag |= BigInt(b & 0x7f) << shift;
      shift += 7n;
    } while (b & 0x80 && pos < ct.length);
    const firstField = Number(tag >> 3n),
      firstWt = Number(tag & 7n);
    if (firstField === 1) {
      if (firstWt === 0) return 3; // pre_key_id varint
      if (firstWt === 2) return 2; // ratchet key bytes
      return null;
    }
    if (firstField === 2 && firstWt === 2) return 3; // no OTK id; base_key bytes
    return null;
  } catch {
    return null;
  }
}

function hexDump(bytes: Uint8Array, n = 32): string {
  return Array.from(bytes.slice(0, n))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}

// Convert 16 raw bytes to a UUID string (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx).
function bytesToUuid(b: Uint8Array): string {
  const h = Array.from(b)
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// SenderKeyMessage header guard: version high-nibble >= 3, field1 tag 0x0a, field1 length 0x10 (16 = UUID).
// Used to extract the distributionId for cold-chain reset in decryptGroupMessage.
function isSkmHeader(ct: Uint8Array): boolean {
  return (
    ct.length >= 19 && ct[0]! >> 4 >= 3 && ct[1] === 0x0a && ct[2] === 0x10
  );
}

import type { ReplyTo } from 'mastodon/api_types/direct_messages';
export type { ReplyTo };

export interface PlaintextPayload {
  text?: string;
  mediaKey?: string;
  mediaIv?: string;
  reply_to?: ReplyTo;
  resend_of_id?: string;
  // Widened from the 'resend_request' literal: decrypted content is untrusted
  // and may carry ANY (incl. future/unknown) msg_type. Consumers MUST treat any
  // present msg_type as a hidden protocol message (invariant C9/C10), never as
  // renderable user content.
  msg_type?: string;
  resend_target_id?: string;
}

export interface CipherOutput {
  ciphertextB64: string;
  type: number;
}

export interface EncryptedBytes {
  bytes: ArrayBuffer;
  key: string;
  iv: string;
}

type ApiRequestFn = <T = unknown>(
  method: string,
  path: string,
  opts?: { data?: Record<string, unknown>; params?: Record<string, string> },
) => Promise<T>;

export interface PublicDeviceBundle {
  device_id: number;
  registration_id: number;
  identity_key: string;
  signed_prekey: { key_id: number; public_key: string; signature: string };
  one_time_prekey: { key_id: number; public_key: string } | null;
  // Extended fields for kyber-prekey support in the server response
  kyber_prekey?: {
    key_id: number;
    public_key: string;
    signature: string;
  } | null;
}

interface DeviceListResponse {
  account_id: string;
  devices: PublicDeviceBundle[];
}

export interface DeviceBundle extends PublicDeviceBundle {
  account_id: string;
  one_time_prekeys: { key_id: number; public_key: string }[];
  kyber_prekeys: { key_id: number; public_key: string; signature: string }[];
}

export interface FanoutEntry {
  recipient_account_id: string;
  recipient_device_id: number;
  ciphertextB64: string;
  type: number;
}

/**
 * Slim-fanout options for PROTOCOL messages (resend-storm fix, 2026-07-27).
 * Content-free pings (acks, resend requests, sibling-resend requests,
 * targeted answers) must not pay the full N+M row cost of a content fanout:
 * on device-accumulated accounts a single decrypt failure produced ~78
 * protocol rows, exhausting the thread endpoint's LIMIT-30 window and
 * blanking the visible conversation (see MESSAGING_DESIGN.md "RESEND STORM").
 *
 *  - skipSelfFanout: peer-only fanout. For acks / cross-user resend
 *    requests — own siblings have no use for a content-free ping.
 *  - onlySelfFanout: self-only fanout. For __sibling_resend__ requests —
 *    the peer's devices are isMine-gated and would ignore the rows anyway.
 *  - onlyRecipientDeviceIds: narrow the recipient set to specific device
 *    ids (applied per account loop). For resend ANSWERS, which only the
 *    requesting device needs.
 */
export interface FanoutOpts {
  skipSelfFanout?: boolean;
  onlySelfFanout?: boolean;
  onlyRecipientDeviceIds?: number[];
  /**
   * H-3: re-pin to a CHANGED remote identity key. When false/absent, any
   * device whose server-served identity key differs from the stored one makes
   * the send throw an E2eeError with `context.code === 'identity_key_changed'`
   * (the OLD session is left intact — nothing is archived or re-pinned).
   * Set this ONLY when re-driving a send after the user has explicitly
   * acknowledged the key change (e.g. after safety-number verification).
   * First contact (no stored key) always establishes silently (TOFU).
   */
  allowIdentityKeyChange?: boolean;
  /**
   * H-3/H-5: PER-TARGET form of allowIdentityKeyChange. Each entry covers
   * exactly one (account, device) — a global flag would let one user
   * acknowledgment silently re-pin OTHER devices that changed keys in the
   * same fanout. An entry bound to `identityKey` applies only while the
   * server still serves that exact key (a stale ack can never approve a
   * later, different key). An acknowledged target is also RE-ESTABLISHED
   * (fresh session onto the acknowledged key) on the next send, and a
   * first-seen device covered by an entry passes the H-5 new-device gate.
   */
  allowIdentityKeyChangeFor?: IdentityChangeAllowance[];
}

/** H-3/H-5: a single user-acknowledged (account, device) identity. */
export interface IdentityChangeAllowance {
  accountId: string;
  deviceId: number;
  identityKey?: string;
}

/** H-3/H-5: shared options for the group SKDM distribution paths. */
export interface IdentityChangeOpts {
  allowIdentityKeyChange?: boolean;
  allowIdentityKeyChangeFor?: IdentityChangeAllowance[];
}

// H-5: the code stamped on the typed error thrown when EVERY device of a
// recipient is gated out of the fanout because it is a first-seen device of
// an already-known contact that the user has not acknowledged yet.
export const NEW_DEVICE_PENDING_CODE = 'e2ee_new_device_pending';

// MD-h: a flushed own-sibling backfill copy. Same shape as FanoutEntry (the
// recipient is always one of our OWN sibling devices) plus threadAccountId —
// the peer/conversation the missed message belongs to, so the caller POSTs the
// ciphertext into the right thread when re-delivering. groupKey is the
// original message's server-minted logical id, stamped onto the queue entry
// after the primary send's POST returned; the caller includes it in the
// re-delivery POST so the copy threads onto the original message.
export interface SiblingBackfillFanout extends FanoutEntry {
  threadAccountId: string;
  groupKey?: string;
}

// Prekey pool sizes. The server serves one OTK + one Kyber per NEW-session
// establishment and deletes them; the pool only refills when THIS device's
// client is online (replenishOneTimePrekeysIfNeeded on conversation-open, 30-min
// debounced), so an offline/idle device only drains. Larger pools + earlier
// replenish give more runway before exhaustion (Kyber depletion HARD-fails
// establishment — kyber_prekeys_depleted — until the owner comes online).
// NB the complete fix for hard-fail-on-empty is a reusable last-resort Kyber
// prekey (graceful degradation); these sizes are the mitigation, not that.
const ONE_TIME_PREKEY_COUNT = 200;
const KYBER_PREKEY_COUNT = 50;
// Replenish when the Kyber pool drops below this — half the pool, so refill
// triggers well before zero with no gap to the next online check.
const KYBER_PREKEY_REPLENISH_THRESHOLD = 25;
const SESSION_MAX_SEND_MS = 7 * 24 * 60 * 60 * 1000;
const SESSION_MAX_RECV_MS = 14 * 24 * 60 * 60 * 1000;
const SESSION_MAX_COUNT = 1000;

export interface SafetyNumber {
  displayable: string;
  scannable: ArrayBuffer;
}

export interface IdentityKeyChange {
  changed: boolean;
  oldKey?: string;
  newKey?: string;
  // Device whose key change is reported (all of the contact's devices are
  // checked; the FIRST mismatch is reported here).
  deviceId?: number;
  // H-5: device ids the server serves for this contact that are NOT in the
  // last-seen set of an already-known contact ("contact added a new device").
  // Absent/empty on first contact (TOFU baseline) and when nothing new.
  newDevices?: number[];
}

export interface GroupCipherOutput {
  ciphertextB64: string;
  type: number;
}

// Result of decryptGroupMessage: the decrypted payload plus the sender-chain
// iteration parsed from the SenderKeyMessage header (field 3), when present.
// Callers can use `iteration` to dedupe server-side replays of an
// already-consumed chain position (L-12).
export type GroupDecryptResult = PlaintextPayload & { iteration?: number };

export interface SenderKeyDistributionEntry {
  recipientAccountId: string;
  recipientDeviceId: number;
  distributionMessageB64: string;
}

interface ServerSKDM {
  sender_account_id: string;
  sender_device_id: number;
  distribution_message: string;
}

interface SessionCleanupResult {
  expired: number;
  evicted: number;
}

interface ScoredSession {
  accountId: string;
  deviceId: number;
  lastActivity: number;
}

let _wasmInitialized = false;
let _wasmInitPromise: Promise<void> | null = null;

export function isWasmSupported(): boolean {
  return typeof WebAssembly !== 'undefined';
}

export async function ensureWasmInit(): Promise<void> {
  if (_wasmInitialized) return;
  if (!isWasmSupported()) {
    throw new Error(
      "Your browser doesn't support WebAssembly, which is required for encrypted messaging. Please use a modern browser (Chrome 57+, Firefox 52+, Safari 11+, Edge 16+).",
    );
  }
  // Share a single in-flight init promise so concurrent callers don't call
  // init() more than once (N13: race condition fix).
  if (!_wasmInitPromise) {
    _wasmInitPromise = (async () => {
      try {
        await init();
        _wasmInitialized = true;
      } catch (error) {
        _wasmInitPromise = null;
        throw new Error(
          `Failed to initialize WebAssembly module for encrypted messaging: ${error instanceof Error ? error.message : String(error)}. Please try refreshing the page or using a different browser.`,
        );
      }
    })();
  }
  await _wasmInitPromise;
}

export class E2eeCrypto {
  store: SignalProtocolStore;
  private api: ApiRequestFn;
  private initialized = false;
  private myAccountId?: string;
  private myDeviceId?: number;
  private client?: SignalClient;
  private lastHealthCheckAt = 0;
  private lastDesyncRecoveryAt = 0;
  // Per-(account:device) cooldown after a failed establish attempt for one of
  // our OWN secondary devices. Sibling-device fan-out is best-effort (forward
  // secrecy only); without this we'd re-fetch the sibling's bundle on every
  // send, burning OTKs and tripping the per-pair bundle throttle (self 429 storm).
  private establishBackoff = new Map<string, number>();
  // Shared in-flight promises so concurrent callers don't trigger duplicate
  // PUT /identity round-trips. Without these, two parallel initIdentity() (or
  // linkNewDevice()) calls each generate a fresh identity / device — the
  // server's find_or_initialize_by overwrites itself (initIdentity) or assigns
  // two next_device_ids (linkNewDevice), in both cases producing irrecoverable
  // local↔server desync.
  private _initIdentityPromise?: Promise<void>;
  private _linkNewDevicePromise?: Promise<number>;
  // DRH-3 single-writer discipline. `clientEpoch` is the store epoch the current
  // in-memory `client` reflects; a mismatch on writer-lock entry means a sibling
  // tab advanced shared ratchet/key state, so the client is stale and must be
  // rebuilt from IDB before operating (reload-before-op). `_localWriterChain`
  // serializes ops in-process when navigator.locks is unavailable.
  private clientEpoch = 0;
  private _localWriterChain: Promise<unknown> = Promise.resolve();
  private static readonly HEALTH_CHECK_INTERVAL_MS = 30 * 60 * 1000;
  // After a FAILED desync self-heal (e.g. the republish PUT was 429'd) retry
  // after this short backoff instead of burning the full 30-min window — a
  // wedged device must not stay wedged for half an hour on one throttled heal.
  private static readonly DESYNC_RETRY_BACKOFF_MS = 60 * 1000;
  // MED-15: max time to WAIT for the cross-tab writer lock before failing an op
  // with a recoverable error (never aborts a running critical section). Generous
  // so normal contention (a sibling doing a legit networked establish) never
  // trips it; only a genuinely stalled holder does. Not readonly so tests can
  // shorten it.
  private static WRITER_LOCK_ACQUIRE_TIMEOUT_MS = 30 * 1000;
  // LOW-8: one-shot guard so the "no Web Locks" degradation warning fires once.
  private static _warnedNoLocks = false;
  // Cooldown before retrying a failed sibling-device establish. Kept at/above
  // the per-pair bundle throttle window (throttle_e2ee_bundle_pair, currently
  // 3/min) so we never re-fetch a broken sibling faster than the server serves.
  private static readonly ESTABLISH_BACKOFF_MS = 60 * 1000;

  constructor(
    api: ApiRequestFn,
    store?: SignalProtocolStore,
    identity?: { accountId: string; deviceId?: number },
  ) {
    this.api = api;
    this.store = store ?? new SignalProtocolStore();
    if (identity) {
      this.myAccountId = identity.accountId;
      this.myDeviceId = identity.deviceId;
    }
  }

  public async getClient(): Promise<SignalClient> {
    return this._getClient();
  }

  private async _getClient(): Promise<SignalClient> {
    await ensureWasmInit();
    if (this.client) return this.client;

    const existingDeviceId =
      this.myDeviceId ?? (await this.store.getMyDeviceId());

    if (!existingDeviceId) {
      throw new Error('cannot create SignalClient without device_id');
    }

    await this.restoreOrCreateClient(existingDeviceId);
    return this.client!;
  }

  // ---- DRH-3 single-writer discipline -------------------------------------
  // Reload-before-op: if a sibling tab advanced the shared state epoch, our
  // in-memory WASM client is stale — drop it (and the store's read cache) so the
  // next _getClient() rebuilds it from the latest persisted IDB state. Runs at
  // the start of every writer-locked critical section. A no-op in the common
  // case where this tab was the last (or only) writer.
  private async syncClientToEpoch(): Promise<void> {
    const epoch = await this.store.getEpoch();
    if (epoch !== this.clientEpoch) {
      this.client = undefined;
      this.store.clearMemCache();
      this.clientEpoch = epoch;
    }
  }

  // Serialize every mutating crypto op across all tabs of this account behind an
  // exclusive Web Lock, and reload the client from IDB whenever a sibling tab
  // committed a mutation (syncClientToEpoch). This is what stops two live tabs
  // (R48) — or a device-collapsed second client (R49) — from diverging the
  // double ratchet: exactly one writer runs at a time, each re-syncs to the
  // latest persisted session before operating, and publishes its own mutation
  // via bumpEpoch on success. Reentrant within one instance (a wrapped method
  // may call another). Falls back to an in-process chain when Web Locks is
  // unavailable — that still prevents intra-tab interleaving, and cross-tab
  // ordering then relies on the epoch reload for sequential ops.
  private async withWriterLock<T>(fn: () => Promise<T>): Promise<T> {
    // NOTE: this must NOT be reentrant. Every public mutating entry point
    // acquires the lock; nested calls between wrapped methods (rekeyGroup →
    // uploadSenderKeyDistribution, fetchAndProcessSenderKeys → decryptMessage)
    // deliberately call the *Locked variants so they run inside the already-held
    // critical section rather than re-requesting the non-reentrant Web Lock. A
    // shared "held" flag was removed here (review HIGH-2): it let an INDEPENDENT
    // op that started while a critical was parked on an await bypass the lock.
    let entered = false;
    const critical = async (): Promise<T> => {
      entered = true;
      await this.syncClientToEpoch();
      // After the reload, our client reflects exactly `epochBefore` mutations.
      const epochBefore = this.clientEpoch;
      const result = await fn();
      const newE = await this.store.bumpEpoch();
      // CAS (HIGH-10): only claim currency if we were the SOLE mutator since the
      // reload — i.e. our own bump is exactly epochBefore+1. If an out-of-band
      // prekey-heal (H, unlocked) committed a mutation between α and γ, the epoch
      // jumped further and our client never incorporated it, so we must NOT adopt
      // the new epoch (that would blind-absorb the heal and skip the reload that
      // picks it up). Keep clientEpoch at epochBefore → next op reloads.
      this.clientEpoch = newE === epochBefore + 1 ? newE : epochBefore;
      return result;
    };
    const locks =
      typeof navigator !== 'undefined'
        ? (navigator as Navigator & { locks?: LockManager }).locks
        : undefined;
    if (locks?.request) {
      // MED-15: bound how long we WAIT to ACQUIRE the lock. A sibling tab that
      // stalls while holding the lock (a slow/hung network request under the
      // lock) would otherwise freeze all E2EE in every other tab indefinitely
      // (navigator.locks has no default timeout). An AbortSignal makes
      // locks.request reject once the wait exceeds the bound; we surface that as
      // a clear, RECOVERABLE error (callers retry / resend) instead of hanging.
      // The signal only affects ACQUISITION — once the lock is granted it is
      // ignored, so we NEVER abort a critical section mid-flight (that could
      // leave a partial mutation and diverge the ratchet). `entered` lets us
      // distinguish a genuine acquisition timeout from a failure inside fn().
      const signal = AbortSignal.timeout(
        E2eeCrypto.WRITER_LOCK_ACQUIRE_TIMEOUT_MS,
      );
      try {
        return (await locks.request(
          `e2ee-writer:${this.myAccountId ?? 'anon'}`,
          { mode: 'exclusive', signal },
          critical,
        )) as T;
      } catch (err) {
        if (!entered && signal.aborted) {
          throw new Error(
            '[e2ee] writer-lock acquisition timed out — a sibling tab may be holding it during a stalled request; retry',
          );
        }
        throw err;
      }
    }
    // LOW-8: no Web Locks (old browser / insecure context). We serialize only
    // within this tab; concurrent cross-tab ops are NOT mutually excluded
    // (residual R48), though the epoch reload still orders sequential cross-tab
    // ops. Surface the degradation once so it is not silent.
    if (!E2eeCrypto._warnedNoLocks) {
      E2eeCrypto._warnedNoLocks = true;
      console.warn(
        '[e2ee] Web Locks API unavailable — cross-tab single-writer discipline is degraded to per-tab ordering; open the app in a single tab for this account.',
      );
    }
    const run = this._localWriterChain.then(critical, critical);
    this._localWriterChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private async restoreOrCreateClient(
    deviceId: number,
  ): Promise<{ kyberCount: number; otkCount: number } | null> {
    const identityRecord = await this.store.loadIdentityRecord();
    const accountId = this.myAccountId ?? 'unknown';

    if (identityRecord) {
      this.client = this.restoreClient(identityRecord, accountId, deviceId);
      this.myDeviceId = deviceId;
      // This freshly (re)built client reflects the current persisted state, so
      // baseline its epoch (DRH-3) — otherwise the first writer-locked op would
      // needlessly rebuild it again.
      this.clientEpoch = await this.store.getEpoch();
      // Import persisted sessions, prekeys and sender keys into the freshly
      // rebuilt WASM client. This MUST run on every client rebuild — not just
      // in initIdentity — because _getClient() rebuilds the in-memory client
      // after a reset (backup restore / destroy) or before initIdentity has
      // finished. Without it, has_session() returns false for every peer, so
      // each send re-establishes a new session (PreKeyMessage / type 3),
      // burning a prekey every time and breaking decryption on both sides.
      return await this.restoreFromStore(this.client);
    }

    this.client = new SignalClient(accountId, deviceId);
    this.myDeviceId = deviceId;
    this.clientEpoch = await this.store.getEpoch();
    return null;
  }

  private restoreClient(
    recordBytes: Uint8Array,
    accountId: string,
    deviceId: number,
  ): SignalClient {
    const data = JSON.parse(new TextDecoder().decode(recordBytes));
    return SignalClient.restore(
      b64Decode(data.identityPublicKey),
      b64Decode(data.identityPrivateKey),
      data.registrationId,
      accountId,
      deviceId,
      data.nextPreKeyId,
      data.nextSignedPreKeyId,
      data.nextKyberPreKeyId,
    );
  }

  private async persistIdentityRecord(): Promise<void> {
    if (!this.client) return;
    const keyPair = this.client.get_identity_key_pair();
    const data = {
      identityPublicKey: b64Encode(keyPair.public_key),
      identityPrivateKey: b64Encode(keyPair.private_key),
      registrationId: this.client.get_registration_id(),
      nextPreKeyId: this.client.get_next_pre_key_id(),
      nextSignedPreKeyId: this.client.get_next_signed_pre_key_id(),
      nextKyberPreKeyId: this.client.get_next_kyber_pre_key_id(),
    };
    keyPair.free();
    const jsonBytes = new TextEncoder().encode(JSON.stringify(data));
    await this.store.saveIdentityRecord(jsonBytes);
    // DRH-3 (review HIGH-3): the prekey-heal paths (replenish / rotate /
    // forceReplacePrekeys / init) advance the WASM key-id counters and persist
    // them here, but run OUTSIDE withWriterLock. Bump the epoch so a sibling tab
    // reloads its (now stale) in-memory counters before its next op — otherwise
    // it could regenerate prekeys from a stale counter and re-use published
    // key_ids (the R47 desync class). We reflect the bump in our own clientEpoch
    // (this client already holds the persisted state) to avoid a needless
    // self-rebuild on our next locked op.
    //
    // CAS (HIGH-10), mirroring withWriterLock: adopt the new epoch ONLY if we
    // were the sole mutator (newE === epochBefore+1). If a concurrent locked op
    // advanced the epoch meanwhile, our client never saw that mutation, so keep
    // the old epoch to force a reload on our next op instead of blind-absorbing.
    const epochBefore = this.clientEpoch;
    const newE = await this.store.bumpEpoch();
    this.clientEpoch = newE === epochBefore + 1 ? newE : epochBefore;
  }

  private async restoreFromStore(
    client: SignalClient,
  ): Promise<{ kyberCount: number; otkCount: number }> {
    const dump = await this.store.dump();
    if (process.env.NODE_ENV !== 'production') {
      const keys = Object.keys(dump);
      console.debug(
        '[E2EE][restore] IDB db=',
        this.store.dbName,
        'total keys=',
        keys.length,
        'prekeys=',
        keys.filter((k) => k.startsWith('prekey:')).length,
        'kyber-prekeys=',
        keys.filter((k) => k.startsWith('kyber-prekey:')).length,
        'signed-prekeys=',
        keys.filter((k) => k.startsWith('signed-prekey:')).length,
        'sessions=',
        keys.filter((k) => k.startsWith('session:')).length,
        'sender-keys=',
        keys.filter((k) => k.startsWith('sender-key:')).length,
        'skdm-received=',
        keys.filter((k) => k.startsWith('skdm:') && !k.startsWith('skdm-init:'))
          .length,
        'skdm-init=',
        keys.filter((k) => k.startsWith('skdm-init:')).length,
      );
    }
    let kyberPreKeyCount = 0;
    let otkCount = 0;
    for (const [key, value] of Object.entries(dump)) {
      if (key.startsWith('prekey:')) {
        const id = parseInt(key.split(':')[1]!, 10);
        const bytes = this.decodeStoredValue(value);
        if (bytes.length > 0) {
          await client.import_pre_key(id, bytes);
          otkCount++;
        }
      } else if (key.startsWith('signed-prekey:')) {
        const id = parseInt(key.split(':')[1]!, 10);
        const bytes = this.decodeStoredValue(value);
        if (bytes.length > 0) {
          await client.import_signed_pre_key(id, bytes);
        }
      } else if (key.startsWith('kyber-prekey:')) {
        const id = parseInt(key.split(':')[1]!, 10);
        const bytes = this.decodeStoredValue(value);
        if (bytes.length > 0) {
          await client.import_kyber_pre_key(id, bytes);
          kyberPreKeyCount++;
        }
      } else if (key.startsWith('session:')) {
        const parts = key.split(':');
        const accId = parts[1]!;
        const devId = parseInt(parts[2]!, 10);
        const bytes = this.decodeStoredValue(value);
        if (bytes.length > 0) {
          await client.import_session(accId, devId, bytes);
        }
      } else if (key.startsWith('sender-key:')) {
        // Restore OWN sender key (used for group message encryption) after
        // page refresh. import_sender_key only populates the encryption store,
        // NOT the decryption store — so this does NOT help with decrypting
        // others' messages. Received sender keys are restored via skdm:* below.
        const parts = key.split(':');
        if (parts.length === 4) {
          const senderAccId = parts[1]!;
          const senderDevId = parseInt(parts[2]!, 10);
          const distributionId = parts[3]!;
          const bytes = this.decodeStoredValue(value);
          if (bytes.length > 0) {
            try {
              await client.import_sender_key(
                senderAccId,
                senderDevId,
                distributionId,
                bytes,
              );
            } catch (importErr) {
              if (process.env.NODE_ENV !== 'production') {
                console.debug(
                  '[E2EE][restore] import_sender_key failed for',
                  senderAccId,
                  distributionId,
                  ':',
                  importErr,
                );
              }
            }
          }
        }
      } else if (key.startsWith('skdm:')) {
        // Restore received sender keys by re-processing the raw SKDM bytes.
        // process_sender_key_distribution (unlike import_sender_key) populates
        // the WASM decryption store that decrypt_group_message reads from.
        const parts = key.split(':');
        if (parts.length === 4) {
          const senderAccId = parts[1]!;
          const senderDevId = parseInt(parts[2]!, 10);
          const distIdFromKey = parts[3]!;
          const bytes = this.decodeStoredValue(value);
          if (bytes.length > 0) {
            if (process.env.NODE_ENV !== 'production') {
              const skdmIter = parseSignalProtobufVarint(bytes, 3);
              console.debug(
                '[E2EE][restore] skdm:',
                key,
                'len=',
                bytes.length,
                'skdmIteration=',
                skdmIter,
                '← if this is > 0, old messages at lower iterations will fail to decrypt',
                'hex=',
                hexDump(bytes, 16),
              );
            }
            try {
              await client.process_sender_key_distribution(
                senderAccId,
                senderDevId,
                bytes,
              );
              if (process.env.NODE_ENV !== 'production') {
                try {
                  const verifyKey = await client.export_sender_key(
                    senderAccId,
                    senderDevId,
                    distIdFromKey,
                  );
                  console.debug(
                    '[E2EE][restore] export_sender_key after restore:',
                    verifyKey
                      ? `${verifyKey.length} bytes ✓`
                      : 'NULL ← distId mismatch?',
                    'sender=',
                    senderAccId,
                    'distId=',
                    distIdFromKey,
                  );
                } catch (exportErr) {
                  console.debug(
                    '[E2EE][restore] export_sender_key threw:',
                    exportErr,
                  );
                }
              }
            } catch (restoreErr) {
              if (process.env.NODE_ENV !== 'production') {
                console.debug(
                  '[E2EE][restore] process_sender_key_distribution failed for',
                  senderAccId,
                  ':',
                  restoreErr,
                );
              }
            }
          }
        }
      }
    }
    return { kyberCount: kyberPreKeyCount, otkCount };
  }

  private decodeStoredValue(value: unknown): Uint8Array {
    if (value instanceof ArrayBuffer) {
      return new Uint8Array(value);
    }
    if (typeof value === 'object' && value !== null && '__bin' in value) {
      return b64Decode((value as { __bin: string }).__bin);
    }
    return new Uint8Array(0);
  }

  getMyDeviceId(): number | undefined {
    return this.myDeviceId;
  }

  private async serverHasOurDevice(deviceId: number): Promise<boolean> {
    try {
      const resp = await this.api<{ devices: { device_id: number }[] }>(
        'GET',
        'v1/e2ee/identity/mine',
      );
      return resp.devices.some((d) => d.device_id === deviceId);
    } catch (err) {
      // L-6: only a 404 means "our device is gone server-side". A network/5xx
      // error must NOT fail open (init would proceed with local state that may
      // be orphaned server-side) — propagate it instead.
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return false;
      throw err;
    }
  }

  // A device revoked server-side (DELETE v1/e2ee/identity/:device_id) keeps
  // complete LOCAL state, so a LIVE tab never notices: it keeps SENDING
  // (encrypting to peers works) while the account's served bundle silently
  // excludes it — it stops RECEIVING with no error, no banner, and no path
  // back short of a reload (the "revoked → dead tab" half of the revocation
  // incident). The reload path already heals this exact state via the R49
  // reclaim (serverHasOurDevice === false → re-register the SAME identity);
  // this brings the same check to the live session. Called from fanoutEncrypt
  // so any real send re-registers the device before it goes out.
  async reRegisterIfRevoked(): Promise<void> {
    if (!this.initialized || this.myDeviceId === undefined) return;
    let serverHasUs: boolean;
    try {
      serverHasUs = await this.serverHasOurDevice(this.myDeviceId);
    } catch (err) {
      // Fail open on a transient /mine failure — a wobbly network must not
      // block sends; the next send retries the check.
      console.warn('[E2EE] revocation check skipped:', err);
      return;
    }
    if (serverHasUs) return;
    console.warn(
      '[E2EE] this device was revoked server-side — reclaiming its registration (same identity)',
    );
    this.initialized = false;
    await this.initIdentity({ force: true });
  }

  async fetchDeviceList(accountId: string): Promise<PublicDeviceBundle[]> {
    try {
      const resp = await this.api<
        DeviceListResponse | (PublicDeviceBundle & { account_id: string })
      >('GET', `v1/e2ee/identity/${accountId}/bundle`);
      if ('devices' in resp) {
        return resp.devices;
      }
      const {
        device_id,
        registration_id,
        identity_key,
        signed_prekey,
        one_time_prekey,
      } = resp;
      return [
        {
          device_id,
          registration_id,
          identity_key,
          signed_prekey,
          one_time_prekey,
        },
      ];
    } catch (err: unknown) {
      // L-4: 404 = recipient has not registered an E2EE identity — expected,
      // return empty. Every other error (5xx, network failure, throttle) must
      // PROPAGATE like fetchIdentityOnly does: swallowing it into [] converts
      // an outage into misleading e2ee_recipient_no_devices /
      // kyber_prekeys_depleted errors and masks the real failure.
      const status =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: unknown }).response === 'object' &&
        (err as { response: { status?: unknown } }).response !== null
          ? (err as { response: { status?: number } }).response.status
          : undefined;
      if (status === 404) {
        return [];
      }
      console.warn(
        '[E2EE] fetchDeviceList unexpected error for',
        accountId,
        '(HTTP',
        status,
        '):',
        err,
      );
      throw err;
    }
  }

  async encryptMultiDevice(
    recipientAccountId: string,
    payload: PlaintextPayload,
    forceReestablish?: boolean,
    opts?: FanoutOpts,
  ): Promise<FanoutEntry[]> {
    return this.withWriterLock(() =>
      this.encryptMultiDeviceLocked(
        recipientAccountId,
        payload,
        forceReestablish,
        opts,
      ),
    );
  }

  private async encryptMultiDeviceLocked(
    recipientAccountId: string,
    payload: PlaintextPayload,
    forceReestablish?: boolean,
    opts?: FanoutOpts,
  ): Promise<FanoutEntry[]> {
    const client = await this._getClient();
    let acctIds = [recipientAccountId];
    if (this.myAccountId && this.myAccountId !== recipientAccountId) {
      if (opts?.onlySelfFanout) {
        // Sibling-targeted protocol (e.g. __sibling_resend__ requests): the
        // peer's devices are isMine-gated and would ignore the rows anyway.
        acctIds = [this.myAccountId];
      } else if (!opts?.skipSelfFanout) {
        acctIds.push(this.myAccountId);
      }
    }

    // MD-h: own sibling device_ids skipped during this fan-out (backed off / no
    // usable bundle inside _devicesForSend, or encryptToDevice threw below).
    // After the fan-out they are enqueued to the durable backfill outbox so a
    // later flushSiblingBackfill re-delivers the copy — the sender's own linked
    // device never permanently misses its own sent message.
    const skippedSiblings: number[] = [];

    const out: FanoutEntry[] = [];
    // H-3/H-5: per-target acknowledgment allowances (see FanoutOpts).
    const allowances = opts?.allowIdentityKeyChangeFor ?? [];
    const allowancesFor = (acctId: string) =>
      allowances.filter((a) => a.accountId === acctId);
    const targetAllowed = (
      acctId: string,
      deviceId: number,
      servedKey: string,
    ): boolean =>
      !!opts?.allowIdentityKeyChange ||
      allowancesFor(acctId).some(
        (a) =>
          a.deviceId === deviceId &&
          (a.identityKey === undefined || a.identityKey === servedKey),
      );
    // Graceful fan-out accounting for the REAL recipient (P3 Hole A): the peer
    // must receive the message on at least ONE device. Track attempts/deliveries
    // so a single broken/stale device is skipped, but a peer where EVERY device
    // failed still surfaces a meaningful error instead of a silent no-op send.
    let recipientDevicesAttempted = 0;
    let recipientDevicesDelivered = 0;
    let lastRecipientErr: unknown = null;
    // H-5: first-seen devices of a KNOWN contact that were gated out of this
    // fanout (not acknowledged). Surfaced via a typed error when they were the
    // ONLY devices the recipient had.
    const blockedNewDevices: number[] = [];
    for (const acctId of acctIds) {
      const isSelfAcct = acctId === this.myAccountId;
      // Only force re-establishment for the recipient — sibling-device fanout
      // rows exist for forward-secrecy and are not broken by the same issue.
      const reestablishThisDevice =
        forceReestablish && acctId === recipientAccountId;
      const acctAllowances = allowancesFor(acctId);
      const ackedDeviceIds = new Set(acctAllowances.map((a) => a.deviceId));
      const devices = await this._devicesForSend(
        client,
        acctId,
        reestablishThisDevice,
        // Collect siblings _devicesForSend dropped (backed off / unestablishable)
        // only for our own account.
        isSelfAcct ? skippedSiblings : undefined,
        undefined,
        {
          allowIdentityKeyChange: opts?.allowIdentityKeyChange,
          allowances: acctAllowances,
          collectBlockedNewDevices:
            acctId === recipientAccountId ? blockedNewDevices : undefined,
        },
      ).then((list) =>
        // Answer targeting (FanoutOpts.onlyRecipientDeviceIds): a resend
        // answer only needs to land on the requesting device, not on every
        // device the account ever linked.
        opts?.onlyRecipientDeviceIds
          ? list.filter((d) =>
              opts.onlyRecipientDeviceIds!.includes(d.device_id),
            )
          : list,
      );
      for (const device of devices) {
        if (isSelfAcct && device.device_id === this.myDeviceId) {
          continue;
        }
        // An acknowledged (account, device) is re-established onto the
        // acknowledged key on this send — the pin was updated at ack time, so
        // without the forced re-establish the stale session to the OLD key
        // would keep being used until the peer's resend flow healed it.
        const ackedDevice = ackedDeviceIds.has(device.device_id);
        const allowChange = targetAllowed(
          acctId,
          device.device_id,
          device.identity_key,
        );
        if (isSelfAcct) {
          // Best-effort sibling fan-out (forward secrecy only). A failure here
          // (e.g. a sibling whose session can't be (re)established) must NOT
          // abort delivery to the real recipient — back off, record it for
          // backfill, and skip it.
          try {
            const ct = await this.encryptToDevice(
              acctId,
              device,
              payload,
              reestablishThisDevice || ackedDevice,
              allowChange,
            );
            out.push({
              recipient_account_id: acctId,
              recipient_device_id: device.device_id,
              ciphertextB64: ct.ciphertextB64,
              type: ct.type,
            });
          } catch (siblingErr) {
            this.establishBackoff.set(
              `${acctId}:${device.device_id}`,
              Date.now() + E2eeCrypto.ESTABLISH_BACKOFF_MS,
            );
            if (!skippedSiblings.includes(device.device_id)) {
              skippedSiblings.push(device.device_id);
            }
            console.warn(
              '[e2ee] sibling-device fan-out failed (best-effort, queued for backfill):',
              acctId,
              device.device_id,
              siblingErr,
            );
          }
          continue;
        }
        // Recipient (peer) device — graceful per-device degradation, mirroring
        // the sibling path above (P3 Hole A). A single broken/stale/depleted
        // recipient device must not abort delivery to the peer's other healthy
        // devices, nor be re-attempted (re-fetching its bundle, burning its
        // prekeys) on every send. Skip + back it off; a TOTAL failure for this
        // recipient (no device delivered) is surfaced after the device loop.
        recipientDevicesAttempted += 1;
        try {
          const ct = await this.encryptToDevice(
            acctId,
            device,
            payload,
            reestablishThisDevice || ackedDevice,
            allowChange,
          );
          out.push({
            recipient_account_id: acctId,
            recipient_device_id: device.device_id,
            ciphertextB64: ct.ciphertextB64,
            type: ct.type,
          });
          recipientDevicesDelivered += 1;
        } catch (recipientErr) {
          lastRecipientErr = recipientErr;
          this.establishBackoff.set(
            `${acctId}:${device.device_id}`,
            Date.now() + E2eeCrypto.ESTABLISH_BACKOFF_MS,
          );
          console.warn(
            '[e2ee] recipient-device fan-out failed (skipped, backed off):',
            acctId,
            device.device_id,
            recipientErr instanceof Error
              ? `${recipientErr.name}: ${recipientErr.message}`
              : String(recipientErr),
            recipientErr,
          );
        }
      }
      // The peer must receive the message on at least one device. If it had
      // devices but every one failed to establish/encrypt, the send is genuinely
      // undeliverable — surface the last (meaningful, e.g. kyber_prekeys_depleted)
      // error rather than returning a fan-out that reaches none of its devices.
      if (
        acctId === recipientAccountId &&
        recipientDevicesAttempted > 0 &&
        recipientDevicesDelivered === 0
      ) {
        throw lastRecipientErr instanceof Error
          ? lastRecipientErr
          : new Error('e2ee_all_recipient_devices_failed');
      }
      // H-5: every device the recipient HAS is a first-seen (unacknowledged)
      // device of a contact we already knew — the fanout was gated to empty.
      // Surface a distinct typed error so the UI can show the "contact added a
      // new device — verify the safety number" trust event instead of the
      // misleading "no devices" copy. Sending must NOT silently proceed.
      if (
        acctId === recipientAccountId &&
        recipientDevicesAttempted === 0 &&
        blockedNewDevices.length > 0
      ) {
        const err = new Error(NEW_DEVICE_PENDING_CODE) as Error & {
          code?: string;
          accountId?: string;
          deviceId?: number;
        };
        err.code = NEW_DEVICE_PENDING_CODE;
        err.accountId = acctId;
        err.deviceId = blockedNewDevices[0];
        throw err;
      }
      // The peer has NO registered E2EE devices at all (fetchIdentityOnly 404 →
      // [] → fetchDeviceList 404 → []; both swallow by design). Throw a typed
      // error so the UI can show the actionable "hasn't enabled encrypted
      // messaging" copy (isMissingRecipientIdentity) — never fall through to
      // an empty fan-out, which the server can only 4xx/5xx on.
      if (acctId === recipientAccountId && recipientDevicesAttempted === 0) {
        const err = new Error('e2ee_recipient_no_devices') as Error & {
          code?: string;
        };
        err.code = 'e2ee_recipient_no_devices';
        throw err;
      }
    }

    // MD-h: durably enqueue the skipped own-sibling copies (best-effort — a
    // backfill-store failure must never abort the successful send to the peer).
    if (skippedSiblings.length > 0) {
      try {
        await this._enqueueSiblingBackfill(
          recipientAccountId,
          payload,
          skippedSiblings,
        );
      } catch (enqErr) {
        console.warn('[e2ee] sibling backfill enqueue failed:', enqErr);
      }
    }

    return out;
  }

  // MD-h: retry spacing for a queued sibling backfill that keeps failing to
  // establish. Bounded so a permanently-unreachable sibling stops retrying.
  private static readonly SIBLING_BACKFILL_MAX_ATTEMPTS = 8;
  private static readonly SIBLING_BACKFILL_BASE_BACKOFF_MS = 60 * 1000;

  private async _enqueueSiblingBackfill(
    recipientAccountId: string,
    payload: PlaintextPayload,
    deviceIds: number[],
  ): Promise<void> {
    const now = Date.now();
    for (const deviceId of deviceIds) {
      await this.store.saveSiblingBackfill({
        recipientAccountId,
        deviceId,
        payload,
        attempts: 0,
        // Eligible immediately — the durable entry IS the record that this
        // copy is owed; flush spacing on repeated FAILURE is applied by
        // notBefore below.
        notBefore: now,
        createdAt: now,
      });
    }
  }

  // Durable retry for a sibling copy that ENCRYPTED fine but whose POST
  // failed after the recipient's copy already delivered — the send must not
  // be reported failed (operator report 2026-08-08: "failed to send" + "seen"
  // on the same message), so the owed copy goes through the same MD-h queue
  // as siblings skipped at fanout time.
  async enqueueSiblingBackfill(
    recipientAccountId: string,
    payload: PlaintextPayload,
    deviceIds: number[],
  ): Promise<void> {
    await this._enqueueSiblingBackfill(recipientAccountId, payload, deviceIds);
  }

  // MD-h: durable outbox inspection (for callers/tests that want to know if a
  // sibling copy is still owed). Returns a snapshot; never mutates.
  async pendingSiblingBackfills(): Promise<SiblingBackfillEntry[]> {
    if (!this.initialized) return [];
    return this.store.loadSiblingBackfills();
  }

  // MD-h repair: stamp the primary send's server-minted group_key onto the
  // queued sibling copies for that thread. Entries are enqueued DURING the
  // fan-out — before the POST returns — so this is how they learn their
  // logical id. Only UNSTAMPED entries are stamped: an entry already carrying
  // a groupKey belongs to an earlier logical message and must keep it.
  async stampSiblingBackfillGroupKey(
    threadAccountId: string,
    groupKey: string,
  ): Promise<void> {
    if (!this.initialized) return;
    await this.withWriterLock(async () => {
      const queued = await this.store.loadSiblingBackfills();
      for (const entry of queued) {
        if (entry.recipientAccountId !== threadAccountId) continue;
        if (entry.groupKey) continue;
        await this.store.saveSiblingBackfill({ ...entry, groupKey });
      }
    });
  }

  // MD-h: re-encrypt every DUE queued sibling copy and return the fanout rows
  // for the caller to POST into each copy's thread. This is PHASE 1 of a
  // two-phase drain: entries are NOT removed here. The caller MUST call
  // confirmSiblingBackfillDelivered() once the POST succeeds — that is what
  // drains the entry. This makes the guarantee durable: if the POST fails after
  // re-encryption, the entry survives (its notBefore is pushed out so it is not
  // re-produced immediately) and is retried on the next flush. Entries whose
  // sibling is still unreachable, or that exceed SIBLING_BACKFILL_MAX_ATTEMPTS,
  // are rescheduled/dropped so the outbox stays bounded.
  async flushSiblingBackfill(): Promise<SiblingBackfillFanout[]> {
    if (!this.initialized) return [];
    return this.withWriterLock(() => this.flushSiblingBackfillLocked());
  }

  // MD-h: PHASE 2 — drain a queued sibling copy after the caller has
  // successfully POSTed its re-delivery. Keyed by the copy's thread + sibling
  // device (matches the outbox key). Idempotent: a no-op if already drained.
  async confirmSiblingBackfillDelivered(
    threadAccountId: string,
    deviceId: number,
  ): Promise<void> {
    if (!this.initialized) return;
    await this.store.removeSiblingBackfill(threadAccountId, deviceId);
  }

  private async flushSiblingBackfillLocked(): Promise<SiblingBackfillFanout[]> {
    const queued = await this.store.loadSiblingBackfills();
    if (queued.length === 0) return [];
    if (!this.myAccountId) return [];

    const now = Date.now();
    const due = queued.filter((e) => e.notBefore <= now);
    if (due.length === 0) return [];

    const client = await this._getClient();
    // Enumerate our own sibling bundles once, forcing re-establishment and
    // bypassing the in-memory establish backoff (the durable entry + notBefore
    // spacing IS the retry gate). This is the ONLY place a self bundle fetch is
    // permitted outside a genuine first establish — it is gated by a due,
    // durable backfill entry, so it cannot storm the self-pair throttle.
    const bundles = await this._devicesForSend(
      client,
      this.myAccountId,
      true, // forceReestablish
      undefined,
      true, // ignoreBackoff
    );
    const bundleByDevice = new Map<number, PublicDeviceBundle>();
    for (const b of bundles) bundleByDevice.set(b.device_id, b);

    const out: SiblingBackfillFanout[] = [];
    for (const entry of due) {
      const bundle = bundleByDevice.get(entry.deviceId);
      // No usable bundle for this sibling (still throttled/offline) — keep the
      // entry for a later attempt; do NOT emit a bogus, undecryptable row.
      if (!bundle || !bundle.signed_prekey.public_key) {
        await this._requeueOrDropBackfill(entry, now);
        continue;
      }
      try {
        const ct = await this.encryptToDevice(
          this.myAccountId,
          bundle,
          entry.payload as PlaintextPayload,
          true,
        );
        out.push({
          recipient_account_id: this.myAccountId,
          recipient_device_id: entry.deviceId,
          ciphertextB64: ct.ciphertextB64,
          type: ct.type,
          threadAccountId: entry.recipientAccountId,
          // The logical id stamped after the primary send's POST — the caller
          // includes it in the re-delivery POST so the copy threads onto the
          // original message. Undefined for pre-repair queue entries.
          groupKey: entry.groupKey,
        });
        // Ciphertext produced for the caller to POST. Do NOT drain here — the
        // caller drains via confirmSiblingBackfillDelivered() only after a
        // successful POST. Reschedule (bounded) so that if the confirm never
        // arrives (POST failed), the copy is retried rather than lost, and a
        // concurrent flush before confirmation won't re-produce it immediately.
        await this._requeueOrDropBackfill(entry, now);
      } catch (err) {
        console.warn(
          '[e2ee] sibling backfill flush failed for device',
          entry.deviceId,
          err,
        );
        await this._requeueOrDropBackfill(entry, now);
      }
    }
    return out;
  }

  private async _requeueOrDropBackfill(
    entry: SiblingBackfillEntry,
    now: number,
  ): Promise<void> {
    const attempts = entry.attempts + 1;
    if (attempts > E2eeCrypto.SIBLING_BACKFILL_MAX_ATTEMPTS) {
      // Give up — the sibling has been unreachable across the full retry
      // schedule. Drop so the outbox cannot grow unbounded.
      await this.store.removeSiblingBackfill(
        entry.recipientAccountId,
        entry.deviceId,
      );
      return;
    }
    // Exponential backoff, capped at ~1h between attempts.
    const delay = Math.min(
      E2eeCrypto.SIBLING_BACKFILL_BASE_BACKOFF_MS * 2 ** (attempts - 1),
      60 * 60 * 1000,
    );
    await this.store.saveSiblingBackfill({
      ...entry,
      attempts,
      notBefore: now + delay,
    });
  }

  // DRH-5c — user-initiated "reset secure session". Re-establishes a fresh
  // outbound Signal session to every device of `peerAccountId` so messaging
  // recovers when auto-recovery has not converged. SAFETY: this is the SAFE
  // manual reset — it NEVER calls store.removeSession (only archive_session,
  // which retains the record) and NEVER touches the plaintext caches
  // (sent:/recv:/replace:), so no decryptable history or un-acked message is
  // ever lost. A fresh PreKey (type-3) session is built from freshly fetched
  // bundles; the peer transparently adopts it on its next inbound message.
  // Returns the number of devices re-established.
  async resetSecureSession(peerAccountId: string): Promise<number> {
    return this.withWriterLock(() =>
      this.resetSecureSessionLocked(peerAccountId),
    );
  }

  private async resetSecureSessionLocked(
    peerAccountId: string,
  ): Promise<number> {
    if (!this.initialized) return 0;
    const client = await this._getClient();
    // forceReestablish=true → full bundles for every device (user-initiated, so
    // the per-device one-time-prekey cost is acceptable and bounded by the user
    // action; no removeSession, no auto-loop).
    const devices = await this._devicesForSend(client, peerAccountId, true);
    let reestablished = 0;
    for (const device of devices) {
      // Skip empty stand-ins (a recipient whose bundle could not be fetched) —
      // establishing against empty key material would throw.
      if (!device.signed_prekey.public_key) continue;
      try {
        // Archive (RETAIN — never removeSession) then establish a fresh session.
        await client.archive_session(peerAccountId, device.device_id);
      } catch {
        /* no-op if no session existed */
      }
      await this.establishSession(
        client,
        peerAccountId,
        device.device_id,
        device,
      );
      const sessionBytes = await client.export_session(
        peerAccountId,
        device.device_id,
      );
      if (sessionBytes) {
        await this.store.saveSession(
          peerAccountId,
          device.device_id,
          sessionBytes,
        );
      }
      await this.store.saveRemoteIdentityKey(
        peerAccountId,
        device.identity_key,
        device.device_id,
      );
      reestablished += 1;
    }
    return reestablished;
  }

  // Returns the device bundle(s) for `accountId` needed by encryptToDevice.
  // Fetches the full prekey bundle (consuming a one-time prekey from the server)
  // only when a new session must be established. When the session is already
  // healthy, returns a minimal identity-only stand-in so existing sessions are
  // reused without burning prekeys.
  private async _devicesForSend(
    client: SignalClient,
    accountId: string,
    forceReestablish?: boolean,
    // MD-h: out-array collecting OWN sibling device_ids that were skipped this
    // call (backed off / no usable bundle) so the caller can enqueue a durable
    // backfill. Only populated for the self account.
    collectSkippedSiblings?: number[],
    // MD-h: bypass the establish-backoff filter (the backfill flush IS the
    // retry, gated by its own durable notBefore, so it must not be re-skipped).
    ignoreBackoff?: boolean,
    // H-3/H-5: per-target acknowledgment + new-device gate controls.
    gateOpts?: {
      allowIdentityKeyChange?: boolean;
      allowances?: IdentityChangeAllowance[];
      collectBlockedNewDevices?: number[];
    },
  ): Promise<PublicDeviceBundle[]> {
    // Enumerate ALL of the account's devices without burning prekeys. Multi-
    // device sends must fan out to every device; keying off device 1 alone
    // silently drops device 2+ once device 1's session is healthy.
    let identityDevices: Array<{ device_id: number; identity_key: string }>;
    try {
      identityDevices = await this.fetchIdentityOnly(accountId);
    } catch (err) {
      // Only treat 404 (peer has no E2EE identity yet) as "fall through to
      // fetchDeviceList". Every other error (5xx, network blip, parse) must
      // propagate — otherwise a transient failure silently calls
      // fetchDeviceList on every retry, burning one of the recipient's
      // one-time prekeys per attempt (OTK-exhaustion vector under flaky
      // network conditions; also masks the real send failure).
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 404) throw err;
      identityDevices = [];
    }
    if (identityDevices.length === 0) {
      // Fallback (e.g. server without identity-only support): full fetch.
      // This is reached only when fetchIdentityOnly enumerated zero devices —
      // either the peer has no E2EE identity (modern server: 404 → [], in which
      // case fetchDeviceList also 404s and burns NO prekey) or a legacy server
      // lacks the identity_key endpoint (then the bundle GET is the only way to
      // enumerate devices at all). It is not gated by per-device needsEstablish
      // because there is no device list to make that decision against.
      // eslint-disable-next-line no-restricted-syntax -- PERMITTED: identity-only enumeration returned no devices; full bundle is the sole enumeration path (404 for no-identity peers consumes no prekey)
      return await this.fetchDeviceList(accountId);
    }

    // Never fan out to our OWN device. It has no self-session, so it would be
    // perpetually flagged needsEstablish and force a bundle fetch against our
    // own account on EVERY send — burning our OTKs and tripping the 1/min
    // per-pair throttle (the self-pair 429 storm). encryptMultiDevice skips it
    // too, but excluding it here avoids the wasted (and throttled) fetch.
    const isSelf = accountId === this.myAccountId;
    if (isSelf) {
      identityDevices = identityDevices.filter(
        (d) => d.device_id !== this.myDeviceId,
      );
      if (identityDevices.length === 0) return [];
    }

    // H-5: new-device gate (contacts only — never our own account). A device
    // id the server serves for an ALREADY-KNOWN contact that we have never
    // seen is a trust event, not silent TOFU: without a gate, anyone holding
    // the account's web session could link a device (H-5) and immediately
    // receive fan-out copies with no client-visible signal. First contact
    // (empty seen set) adopts the served set silently (TOFU); later additions
    // are excluded from the fanout unless explicitly covered by a per-target
    // acknowledgment allowance (or the global flag). The seen set is updated
    // ONLY at first contact and at explicit acknowledgment — never here.
    if (!isSelf) {
      const seen = await this.store.loadSeenDeviceIds(accountId);
      if (seen.length === 0) {
        await this.store.saveSeenDeviceIds(
          accountId,
          identityDevices.map((d) => d.device_id),
        );
      } else {
        const blocked: number[] = [];
        identityDevices = identityDevices.filter((idev) => {
          if (seen.includes(idev.device_id)) return true;
          const covered =
            !!gateOpts?.allowIdentityKeyChange ||
            (gateOpts?.allowances ?? []).some(
              (a) =>
                a.deviceId === idev.device_id &&
                (a.identityKey === undefined ||
                  a.identityKey === idev.identity_key),
            );
          if (!covered) blocked.push(idev.device_id);
          return covered;
        });
        if (blocked.length > 0) {
          gateOpts?.collectBlockedNewDevices?.push(...blocked);
          if (identityDevices.length === 0) return [];
        }
      }
    }

    // Devices explicitly acknowledged via a per-target allowance are
    // re-established on this send (fresh session onto the acknowledged key).
    const ackedDeviceIds = new Set(
      (gateOpts?.allowances ?? []).map((a) => a.deviceId),
    );

    // Decide per-device whether (re-)establishment is needed. The identity-key
    // comparison is per-device (storedIdKey keyed by account+device) so a
    // device-2 identity never trips a false "changed" for device 1.
    const now = Date.now();
    const decisions = await Promise.all(
      identityDevices.map(async ({ device_id, identity_key }) => {
        const existing = await client.has_session(accountId, device_id);
        const storedIdKey = await this.store.loadRemoteIdentityKey(
          accountId,
          device_id,
        );
        const idKeyChanged =
          !!existing && !!storedIdKey && storedIdKey !== identity_key;
        const needsEstablish =
          !!forceReestablish ||
          ackedDeviceIds.has(device_id) ||
          idKeyChanged ||
          !existing;
        // A device whose recent establish attempt failed is backed off for a
        // cooldown so we stop re-fetching its bundle (burning prekeys) on every
        // send — for BOTH our own siblings (best-effort forward secrecy) and a
        // peer's broken/stale device (graceful fan-out, P3 Hole A). The guard
        // below ensures a real recipient is never fully hidden by this.
        const backedOff =
          needsEstablish &&
          (this.establishBackoff.get(`${accountId}:${device_id}`) ?? 0) > now;
        return {
          device_id,
          identity_key,
          storedIdKey,
          needsEstablish,
          backedOff,
        };
      }),
    );

    let sendable = decisions.filter((d) => !d.backedOff || ignoreBackoff);
    // Never let the backoff hide EVERY device of a REAL recipient: that would
    // silently turn a broken-but-present peer into a no-op send (delivered
    // nowhere). If all of a recipient's devices are backed off, attempt them
    // anyway — encryptMultiDevice surfaces a meaningful error if they all still
    // fail. Our own siblings MAY be fully skipped (best-effort forward secrecy).
    if (sendable.length === 0 && !isSelf && decisions.length > 0) {
      sendable = decisions;
    }

    // Only fetch full bundles (which burn a one-time prekey per device) when at
    // least one sendable device actually needs establishing.
    const anyNeedsEstablish = sendable.some((d) => d.needsEstablish);
    let fullDevices: PublicDeviceBundle[] | null = null;
    if (anyNeedsEstablish) {
      if (isSelf) {
        // Sibling fan-out is BEST-EFFORT (forward secrecy only): a throttled or
        // failed fetch of our OWN bundle must never abort the send to the real
        // recipient. Degrade to "no usable bundle" — the skip path below backs
        // the sibling off and (via collectSkippedSiblings) queues the durable
        // MD-h backfill. Bundle-fetch errors for a RECIPIENT still propagate
        // (L-4): masking them produced misleading no-devices/depleted errors.
        try {
          // eslint-disable-next-line no-restricted-syntax -- PERMITTED: fetchDeviceList only when a device needs establishing
          fullDevices = await this.fetchDeviceList(accountId);
        } catch (selfBundleErr) {
          console.warn(
            '[e2ee] self bundle fetch failed (best-effort sibling fan-out degraded):',
            selfBundleErr,
          );
        }
      } else {
        // eslint-disable-next-line no-restricted-syntax -- PERMITTED: fetchDeviceList only when a device needs establishing (idKeyChanged || !existing || forceReestablish)
        fullDevices = await this.fetchDeviceList(accountId);
      }
    }

    const out: PublicDeviceBundle[] = [];
    for (const d of sendable) {
      const standIn: PublicDeviceBundle = {
        device_id: d.device_id,
        registration_id: 0,
        identity_key: d.storedIdKey ?? d.identity_key,
        signed_prekey: { key_id: 0, public_key: '', signature: '' },
        one_time_prekey: null,
      };
      if (!d.needsEstablish) {
        // Healthy device — identity-only stand-in (no prekey burn).
        // encryptToDevice reuses the existing session and persists the id key.
        out.push(standIn);
        continue;
      }
      const full = fullDevices?.find((x) => x.device_id === d.device_id);
      if (full) {
        out.push(full);
        continue;
      }
      // needsEstablish but no usable bundle (the server no longer serves this
      // device). Do NOT fabricate an empty stand-in: encryptToDevice would call
      // establishSession with empty key material and throw
      // kyber_prekeys_depleted, aborting the whole fan-out (including the real
      // recipient). (A throttled/failed bundle fetch no longer lands here —
      // fetchDeviceList rethrows non-404 errors, L-4.)
      if (isSelf) {
        // Our own sibling — drop it and back off so we stop re-fetching its
        // bundle every send. Forward secrecy to that sibling is best-effort.
        // MD-h: record this device_id so the caller can enqueue a durable
        // backfill entry and retry once the cooldown clears.
        collectSkippedSiblings?.push(d.device_id);
        this.establishBackoff.set(
          `${accountId}:${d.device_id}`,
          now + E2eeCrypto.ESTABLISH_BACKOFF_MS,
        );
        continue;
      }
      // The actual recipient with no establishable bundle — keep the empty
      // stand-in so the existing meaningful error still surfaces to the user.
      out.push(standIn);
    }
    return out;
  }

  private async encryptToDevice(
    accountId: string,
    device: PublicDeviceBundle,
    payload: PlaintextPayload,
    forceReestablish?: boolean,
    allowIdentityKeyChange?: boolean,
  ): Promise<CipherOutput> {
    let client = await this._getClient();
    const existing = await client.has_session(accountId, device.device_id);
    const storedIdKey = await this.store.loadRemoteIdentityKey(
      accountId,
      device.device_id,
    );
    // H-3: a stored remote identity key that DIFFERS from the server-served one
    // is a trust event, not an auto-recovery trigger. Never silently archive the
    // session and re-pin — that is the MITM path. First contact (no stored key)
    // still establishes silently (TOFU); re-pinning requires the caller to pass
    // allowIdentityKeyChange after explicit user acknowledgment.
    const idKeyChanged = !!storedIdKey && storedIdKey !== device.identity_key;
    if (idKeyChanged && !allowIdentityKeyChange) {
      throw new E2eeError(
        'encrypt_message',
        {
          code: 'identity_key_changed',
          accountId,
          deviceId: device.device_id,
        },
        new Error(
          `remote identity key changed for ${accountId}:${device.device_id} — send blocked pending user acknowledgment`,
        ),
      );
    }
    const needsEstablish = forceReestablish || idKeyChanged || !existing;
    let justEstablished = false;
    if (needsEstablish) {
      if (existing && allowIdentityKeyChange) {
        // Acknowledged re-key: archive alone does NOT unblock the new identity.
        // The WASM client pins a contact's identity from any session it built
        // NATIVELY in this realm (active or archived — there is no
        // delete_session API), and process_pre_key_bundle onto the changed key
        // is then refused with an opaque "Operation failed" forever. (The ack
        // updates the stored pin, so idKeyChanged is already false here —
        // the allowance itself is the signal that the old session is pinned
        // to a superseded identity.) Remove the session from the store and
        // rebuild the client from IDB so the stale pin is gone before
        // establishing onto the acknowledged key.
        try {
          await client.archive_session(accountId, device.device_id);
        } catch {
          /* no-op if no session existed */
        }
        try {
          // eslint-disable-next-line no-restricted-syntax -- PERMITTED: removeSession in acknowledged identity-key-change path, immediately before establishing onto the acked key
          await this.store.removeSession(accountId, device.device_id);
        } catch {
          /* best-effort */
        }
        this.client = undefined;
        this.store.clearMemCache();
        client = await this._getClient();
      } else if (existing) {
        try {
          await client.archive_session(accountId, device.device_id);
        } catch {
          /* no-op if no session existed */
        }
        if (forceReestablish) {
          try {
            // eslint-disable-next-line no-restricted-syntax -- PERMITTED: removeSession in explicit resend re-establishment path, immediately before creating replacement session
            await this.store.removeSession(accountId, device.device_id);
          } catch {
            /* best-effort */
          }
        }
      }
      await this.establishSession(client, accountId, device.device_id, device);
      justEstablished = true;
    } else if (existing && !storedIdKey) {
      // Session is good — just persist the identity key without burning a prekey.
      await this.store.saveRemoteIdentityKey(
        accountId,
        device.identity_key,
        device.device_id,
      );
    }
    const buf = utf8Encode(JSON.stringify(payload));
    let wasmCt;
    try {
      wasmCt = await wrapWasm(
        'encrypt_message',
        { peer: accountId, device_id: device.device_id, attempt: 1 },
        () => client.encrypt_message(accountId, device.device_id, buf),
      );
    } catch (encryptErr) {
      if (justEstablished) {
        // Fresh session just established — don't burn another prekey in recovery.
        console.error(
          '[e2ee] encrypt_message failed immediately after session establishment:',
          encryptErr,
        );
        throw encryptErr;
      }
      // Session state is corrupted (ratchet diverged) — archive it, fetch fresh
      // prekeys from the server, re-establish via X3DH, then retry once.
      console.error(
        '[e2ee] encrypt_message failed, attempting auto-recovery:',
        encryptErr,
      );
      try {
        await client.archive_session(accountId, device.device_id);
      } catch {
        /* best-effort */
      }
      try {
        // eslint-disable-next-line no-restricted-syntax -- PERMITTED: removeSession in auto-recovery, immediately before re-establishing replacement session
        await this.store.removeSession(accountId, device.device_id);
      } catch {
        /* best-effort */
      }
      // eslint-disable-next-line no-restricted-syntax -- PERMITTED: fetchDeviceList in auto-recovery path to obtain fresh bundle for re-establishment
      const freshDevices = await this.fetchDeviceList(accountId);
      const freshDevice = freshDevices.find(
        (d) => d.device_id === device.device_id,
      );
      if (!freshDevice) {
        // L-9: the fresh fetch failed (fetchDeviceList now rethrows non-404s)
        // or no longer serves this device — do NOT re-establish with the stale
        // bundle, which would produce a session the peer can never decrypt.
        throw encryptErr;
      }
      // H-3 (recovery leg): re-establishing onto a CHANGED identity key is the
      // same silent re-pin as the send path — block it identically.
      if (
        storedIdKey &&
        storedIdKey !== freshDevice.identity_key &&
        !allowIdentityKeyChange
      ) {
        throw new E2eeError(
          'encrypt_message',
          {
            code: 'identity_key_changed',
            accountId,
            deviceId: device.device_id,
          },
          new Error(
            `remote identity key changed for ${accountId}:${device.device_id} — auto-recovery blocked pending user acknowledgment`,
          ),
        );
      }
      await this.establishSession(
        client,
        accountId,
        device.device_id,
        freshDevice,
      );
      wasmCt = await wrapWasm(
        'encrypt_message',
        { peer: accountId, device_id: device.device_id, attempt: 2 },
        () => client.encrypt_message(accountId, device.device_id, buf),
      );
    }
    try {
      await this.store.touchSessionTimestamp(
        accountId,
        device.device_id,
        'send',
      );
      const sessionBytes = await client.export_session(
        accountId,
        device.device_id,
      );
      if (sessionBytes) {
        await this.store.saveSession(accountId, device.device_id, sessionBytes);
      }
      return {
        ciphertextB64: b64Encode(wasmCt.body),
        type: wasmCt.message_type,
      };
    } finally {
      wasmCt.free();
    }
  }

  private async establishSession(
    client: SignalClient,
    recipientAccountId: string,
    recipientDeviceId: number,
    device: PublicDeviceBundle,
  ): Promise<void> {
    const kyber = device.kyber_prekey;
    const kyberPreKeyId = kyber?.key_id ?? 0;
    const kyberPreKey = kyber ? b64Decode(kyber.public_key) : new Uint8Array(0);
    const kyberSignature = kyber
      ? b64Decode(kyber.signature)
      : new Uint8Array(0);

    // L-3: dev-only — previously an ungated console.log in all builds.
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[e2ee] establishSession params:', {
        recipientAccountId,
        recipientDeviceId,
        hasKyber: !!kyber,
        kyberPreKeyId,
        kyberPreKeyLen: kyberPreKey.length,
        hasSignedPrekey: !!device.signed_prekey.public_key,
        hasOneTimePrekey: !!device.one_time_prekey,
        identityKeyPrefix: device.identity_key.slice(0, 12),
        spkId: device.signed_prekey.key_id,
        registrationId: device.registration_id,
      });
    }

    if (!kyber) {
      // The WASM process_pre_key_bundle requires non-empty Kyber params (no optional
      // path). Passing empty Uint8Array(0) produces an opaque "Operation failed".
      // Surface a typed error so callers can show a meaningful message.
      const err = new Error('kyber_prekeys_depleted');
      (err as Error & { code: string }).code = 'kyber_prekeys_depleted';
      console.error(
        '[e2ee] establishSession: recipient has no Kyber prekeys — bundle served without PQ material.',
        { recipientAccountId, recipientDeviceId },
        'They need to open their messages tab to replenish prekeys.',
      );
      throw err;
    }

    await wrapWasm(
      'process_pre_key_bundle',
      {
        peer: recipientAccountId,
        device_id: recipientDeviceId,
        registration_id: device.registration_id,
        signed_prekey_id: device.signed_prekey.key_id,
        one_time_prekey_id: device.one_time_prekey?.key_id ?? undefined,
        kyber_prekey_id: kyberPreKeyId,
      },
      () =>
        client.process_pre_key_bundle(
          recipientAccountId,
          recipientDeviceId,
          device.registration_id,
          b64Decode(device.identity_key),
          device.signed_prekey.key_id,
          b64Decode(device.signed_prekey.public_key),
          b64Decode(device.signed_prekey.signature),
          device.one_time_prekey?.key_id ?? null,
          device.one_time_prekey
            ? b64Decode(device.one_time_prekey.public_key)
            : null,
          kyberPreKeyId,
          kyberPreKey,
          kyberSignature,
        ),
    );
    const sessionBytes = await client.export_session(
      recipientAccountId,
      recipientDeviceId,
    );
    if (sessionBytes) {
      await this.store.saveSession(
        recipientAccountId,
        recipientDeviceId,
        sessionBytes,
      );
    }
    await this.store.touchSessionTimestamp(
      recipientAccountId,
      recipientDeviceId,
      'send',
    );
    // Remember the identity key (per device) so we can detect future regeneration.
    await this.store.saveRemoteIdentityKey(
      recipientAccountId,
      device.identity_key,
      recipientDeviceId,
    );
  }

  // Failure backoff for init. Every send/poll/streaming path funnels into
  // initIdentity via ensureE2eeReady, which forgets its memoized promise on
  // failure — so when registration fails (e.g. PUT /v1/e2ee/identity rate-
  // limited), callers used to re-run the FULL init every few seconds, each
  // attempt regenerating 200 one-time + 50 Kyber prekeys into a WASM heap
  // that never shrinks (~4.5GB balloons; froze the no-swap prod box,
  // 2026-07-31). Exponential: 5s doubling to a 5min cap; cleared on success.
  // Instance-level on purpose: a gate restore rebuilds the singleton, so a
  // successful user-driven recovery starts clean.
  private _initFailures = 0;
  private _initBackoffUntil = 0;

  async initIdentity(opts?: { force?: boolean }): Promise<void> {
    if (this.initialized) return;
    if (this._initIdentityPromise) return this._initIdentityPromise;
    if (opts?.force) {
      this._initFailures = 0;
      this._initBackoffUntil = 0;
    } else if (Date.now() < this._initBackoffUntil) {
      throw new Error('e2ee_init_backoff: retrying too soon after a failure');
    }
    this._initIdentityPromise = this._initIdentityImpl()
      .then(
        () => {
          this._initFailures = 0;
          this._initBackoffUntil = 0;
        },
        (err: unknown) => {
          this._initFailures += 1;
          const delay = Math.min(
            5_000 * 2 ** (this._initFailures - 1),
            300_000,
          );
          this._initBackoffUntil = Date.now() + delay;
          throw err;
        },
      )
      .finally(() => {
        this._initIdentityPromise = undefined;
      });
    return this._initIdentityPromise;
  }

  private async _initIdentityImpl(): Promise<void> {
    await ensureWasmInit();

    const existingDeviceId =
      this.myDeviceId ?? (await this.store.getMyDeviceId());
    const existingIdentityRecord = await this.store.loadIdentityRecord();

    const hasCompleteLocalState =
      !!existingIdentityRecord && existingDeviceId !== undefined;

    if (hasCompleteLocalState) {
      const serverHasUs = await this.serverHasOurDevice(existingDeviceId!);
      if (serverHasUs) {
        const counts = await this.restoreOrCreateClient(existingDeviceId!);
        const { kyberCount, otkCount } = counts ?? {
          kyberCount: 0,
          otkCount: 0,
        };
        if (kyberCount === 0 || otkCount === 0) {
          // Partial IDB clear detected: identity record survived but prekey
          // private keys (Kyber and/or OTK) were wiped. The server still holds
          // stale public prekeys whose private halves are gone — any incoming
          // PreKeyMessage encrypted to those keys will produce "Operation
          // failed" in the WASM. Replace ALL server prekey material with fresh
          // keys so senders fetch a bundle we can actually decrypt.
          await this.forceReplacePrekeys();
        }
        this.initialized = true;
        // Restore-from-backup poison check (resend-storm root defect): the
        // count guard above is all-or-nothing, but a backup is a point-in-time
        // dump — the lost device keeps replenishing afterwards, so the SERVER
        // pool can advertise prekey ids whose private halves the restored
        // backup lacks while local counts stay positive. Every type-3 built
        // from that pool fails to decrypt, deterministically, and the resulting
        // bogus sessions never self-heal. Cross-check the server's advertised
        // ids against our local private records at EVERY init (one
        // non-destructive GET; no-op when consistent) so a poisoned pool is
        // replaced before any peer fetches it.
        await this.crossCheckServerPrekeyIds();
        await this.replenishOneTimePrekeysIfNeeded();
        await this.rotateSignedPrekeyIfNeeded();
        return;
      }
      this.myDeviceId = existingDeviceId;
    }

    const deviceId = existingDeviceId ?? 1;
    const accountId = this.myAccountId ?? 'unknown';

    // Reclaim restore (R49 branch 3): a complete local identity record exists
    // (hydrated from backup) but the server has no device row (revoked/wiped).
    // Rebuild the client from the PERSISTED identity record instead of minting
    // a fresh SignalClient — a new identity key would orphan every hydrated
    // session and every peer's existing session to us, breaking decrypt in
    // both directions and firing spurious key-change banners. Fresh prekey
    // material is still generated and uploaded (bound to the SAME identity);
    // hydrated private prekeys stay in the store so in-flight PreKeyMessages
    // sent to the old bundle remain decryptable.
    if (hasCompleteLocalState) {
      await this.restoreOrCreateClient(deviceId);
    } else {
      this.client = new SignalClient(accountId, deviceId);
      this.myDeviceId = deviceId;
    }

    // Generate one-time prekeys — captured for upload
    const preKeys = this.client.generate_pre_keys(
      ONE_TIME_PREKEY_COUNT,
    ) as WasmPreKey[];

    // Generate signed prekey — captured for upload
    const signedPreKey = this.client.generate_signed_pre_key();

    // Generate kyber prekeys — captured for upload
    const kyberPreKeys = this.client.generate_kyber_pre_keys(
      KYBER_PREKEY_COUNT,
    ) as WasmKyberPreKey[];

    // Get identity public key for upload
    const identityPublicKey = this.client.get_identity_public_key();
    const registrationId = this.client.get_registration_id();

    // Build upload body
    const body: Record<string, unknown> = {
      registration_id: registrationId,
      identity_key: b64Encode(identityPublicKey),
      signed_prekey_id: signedPreKey.id,
      signed_prekey_public: b64Encode(signedPreKey.public_key),
      signed_prekey_signature: b64Encode(signedPreKey.signature),
      one_time_prekeys: preKeys.map((pk) => ({
        key_id: pk.id,
        public_key: b64Encode(pk.public_key),
      })),
      kyber_prekeys: kyberPreKeys.map((pk) => ({
        key_id: pk.id,
        public_key: b64Encode(pk.public_key),
        signature: b64Encode(pk.signature),
      })),
    };
    if (this.myDeviceId !== undefined) body.device_id = this.myDeviceId;

    // DRH-2: verify the full registration bundle round-trips locally BEFORE
    // publishing it, so a corrupt/inconsistent bundle fails loudly here instead
    // of becoming the only thing peers can fetch (opaque "Operation failed").
    // Free the generated WASM keys if we bail so they don't leak.
    try {
      await this.selfTestBundle(body);
    } catch (err) {
      for (const pk of preKeys) pk.free();
      signedPreKey.free();
      for (const pk of kyberPreKeys) pk.free();
      throw err;
    }

    let resp: { device_id?: number };
    try {
      resp = await this.api<{ device_id?: number }>('PUT', 'v1/e2ee/identity', {
        data: body,
      });
    } catch (err) {
      // Registration failed (throttled/offline): the freshly generated WASM
      // key objects would otherwise never be freed — they are only freed in
      // the success path's store-save loops below. Leaking them compounds
      // the retry-balloon this backoff exists to stop.
      for (const pk of preKeys) pk.free();
      signedPreKey.free();
      for (const pk of kyberPreKeys) pk.free();
      throw err;
    }

    const assignedDeviceId = resp.device_id ?? this.myDeviceId ?? 1;
    await this.store.storeMyDeviceId(assignedDeviceId);
    this.myDeviceId = assignedDeviceId;

    // Persist identity record to store
    await this.persistIdentityRecord();

    // Persist all prekey records
    for (const pk of preKeys) {
      await this.store.savePreKey(pk.id, pk.record);
      pk.free();
    }
    await this.store.saveSignedPreKey(signedPreKey.id, signedPreKey.record);
    signedPreKey.free();
    for (const pk of kyberPreKeys) {
      await this.store.saveKyberPreKey(pk.id, pk.record);
      pk.free();
    }

    await this.replenishOneTimePrekeysIfNeeded();
    await this.rotateSignedPrekeyIfNeeded();

    this.initialized = true;
  }

  // Provision THIS client as an additional ("linked") device for an account
  // that already has a registered device. Unlike initIdentity's generate path,
  // it deliberately omits device_id so the server assigns next_device_id —
  // registering a NEW device instead of overwriting device 1 — and rebuilds the
  // WASM client with the server-assigned device_id so session addressing
  // (sender_device_id) matches. Generates its own identity; does NOT adopt the
  // account backup (linked devices are not the backup's primary). Fixes R49.
  async linkNewDevice(): Promise<number> {
    // Already linked → return the existing assignment, no re-register.
    if (this.initialized && this.myDeviceId !== undefined) {
      return this.myDeviceId;
    }
    if (this._linkNewDevicePromise) return this._linkNewDevicePromise;
    this._linkNewDevicePromise = this._linkNewDeviceImpl().finally(() => {
      this._linkNewDevicePromise = undefined;
    });
    return this._linkNewDevicePromise;
  }

  private async _linkNewDeviceImpl(): Promise<number> {
    await ensureWasmInit();
    const accountId = this.myAccountId ?? 'unknown';

    // Temp client to generate identity + prekeys (all device_id-independent).
    const tempClient = new SignalClient(accountId, 1);
    const preKeys = tempClient.generate_pre_keys(
      ONE_TIME_PREKEY_COUNT,
    ) as WasmPreKey[];
    const signedPreKey = tempClient.generate_signed_pre_key();
    const kyberPreKeys = tempClient.generate_kyber_pre_keys(
      KYBER_PREKEY_COUNT,
    ) as WasmKyberPreKey[];
    const identityPublicKey = tempClient.get_identity_public_key();
    const registrationId = tempClient.get_registration_id();

    const linkBody: Record<string, unknown> = {
      registration_id: registrationId,
      identity_key: b64Encode(identityPublicKey),
      signed_prekey_id: signedPreKey.id,
      signed_prekey_public: b64Encode(signedPreKey.public_key),
      signed_prekey_signature: b64Encode(signedPreKey.signature),
      one_time_prekeys: preKeys.map((pk) => ({
        key_id: pk.id,
        public_key: b64Encode(pk.public_key),
      })),
      kyber_prekeys: kyberPreKeys.map((pk) => ({
        key_id: pk.id,
        public_key: b64Encode(pk.public_key),
        signature: b64Encode(pk.signature),
      })),
    };

    // DRH-2: self-test the new device's full bundle before registering it, so a
    // corrupt bundle never becomes a device peers can't establish against. Free
    // the temp client's generated keys (and the client) if we bail.
    try {
      await this.selfTestBundle(linkBody);
    } catch (err) {
      for (const pk of preKeys) pk.free();
      signedPreKey.free();
      for (const pk of kyberPreKeys) pk.free();
      tempClient.free();
      throw err;
    }

    // Register WITHOUT device_id → server assigns next_device_id.
    const resp = await this.api<{ device_id?: number }>(
      'PUT',
      'v1/e2ee/identity',
      { data: linkBody },
    );

    const assignedDeviceId = resp.device_id;
    if (assignedDeviceId === undefined) {
      throw new Error('server did not assign a device_id for the new device');
    }

    // Persist identity record (keypair + counters) and prekeys from the temp
    // client, then store the assigned device_id.
    this.client = tempClient;
    this.myDeviceId = assignedDeviceId;
    await this.persistIdentityRecord();
    await this.store.storeMyDeviceId(assignedDeviceId);
    await this.store.save('linked-device', true);
    for (const pk of preKeys) {
      await this.store.savePreKey(pk.id, pk.record);
      pk.free();
    }
    await this.store.saveSignedPreKey(signedPreKey.id, signedPreKey.record);
    signedPreKey.free();
    for (const pk of kyberPreKeys) {
      await this.store.saveKyberPreKey(pk.id, pk.record);
      pk.free();
    }

    // Rebuild the live client with the SERVER-ASSIGNED device_id so its self
    // address matches what peers use (sender_device_id). restoreOrCreateClient
    // re-imports the prekeys we just persisted.
    this.client = undefined;
    await this.restoreOrCreateClient(assignedDeviceId);

    await this.replenishOneTimePrekeysIfNeeded();
    await this.rotateSignedPrekeyIfNeeded();

    this.initialized = true;
    return assignedDeviceId;
  }

  private async replenishOneTimePrekeysIfNeeded(): Promise<void> {
    if (!this.client || !this.myDeviceId) return;

    try {
      const mineResp = await this.api<{
        devices: Array<{
          device_id: number;
          prekeys_remaining: number;
          kyber_prekeys_remaining?: number;
          has_last_resort_kyber?: boolean;
        }>;
      }>('GET', 'v1/e2ee/identity/mine');
      const myDeviceInfo = mineResp.devices.find(
        (d) => d.device_id === this.myDeviceId,
      );
      if (!myDeviceInfo) return;

      const needsOtk = myDeviceInfo.prekeys_remaining < ONE_TIME_PREKEY_COUNT;
      // Only replenish kyber if the server reports the count (undefined = server doesn't support yet).
      // Trigger at KYBER_PREKEY_REPLENISH_THRESHOLD so we top up before hitting zero.
      const kyberRemaining = myDeviceInfo.kyber_prekeys_remaining;
      const needsKyber =
        kyberRemaining !== undefined &&
        kyberRemaining < KYBER_PREKEY_REPLENISH_THRESHOLD;
      // Backfill the reusable last-resort Kyber prekey when the server reports it
      // is missing (a fresh registration, or an account from before the feature).
      // `undefined` means the server predates the field — don't spuriously
      // publish in that case (=== false only).
      const needsLastResort = myDeviceInfo.has_last_resort_kyber === false;

      if (!needsOtk && !needsKyber && !needsLastResort) return;

      const newPreKeys = needsOtk
        ? (this.client.generate_pre_keys(ONE_TIME_PREKEY_COUNT) as WasmPreKey[])
        : null;
      // Top up to KYBER_PREKEY_COUNT rather than always generating a fixed batch.
      const kyberToGenerate = needsKyber
        ? Math.max(KYBER_PREKEY_COUNT - (kyberRemaining ?? 0), 1)
        : 0;
      const newKyberKeys = needsKyber
        ? (this.client.generate_kyber_pre_keys(
            kyberToGenerate,
          ) as WasmKyberPreKey[])
        : null;
      // One reusable last-resort Kyber prekey — generated identically (the WASM
      // draws no one-time/reusable distinction; the server simply never deletes
      // it on serve).
      const lastResortKyber = needsLastResort
        ? this.client.generate_kyber_pre_key()
        : null;

      const uploadData: Record<string, unknown> = {
        device_id: this.myDeviceId,
      };
      if (newPreKeys) {
        uploadData.one_time_prekeys = newPreKeys.map((pk) => ({
          key_id: pk.id,
          public_key: b64Encode(pk.public_key),
        }));
      }
      if (newKyberKeys) {
        uploadData.kyber_prekeys = newKyberKeys.map((pk) => ({
          key_id: pk.id,
          public_key: b64Encode(pk.public_key),
          signature: b64Encode(pk.signature),
        }));
      }
      if (lastResortKyber) {
        uploadData.last_resort_kyber_prekey = {
          key_id: lastResortKyber.id,
          public_key: b64Encode(lastResortKyber.public_key),
          signature: b64Encode(lastResortKyber.signature),
        };
      }
      await this.api('POST', 'v1/e2ee/identity/prekeys', { data: uploadData });

      if (newPreKeys) {
        for (const pk of newPreKeys) {
          await this.store.savePreKey(pk.id, pk.record);
          pk.free();
        }
      }
      if (newKyberKeys) {
        for (const pk of newKyberKeys) {
          await this.store.saveKyberPreKey(pk.id, pk.record);
          pk.free();
        }
      }
      if (lastResortKyber) {
        // Persist the private half; it is retained forever (no removal path) and
        // re-imported on every client rebuild, so the reused key always decrypts.
        await this.store.saveKyberPreKey(
          lastResortKyber.id,
          lastResortKyber.record,
        );
        lastResortKyber.free();
      }

      // CRITICAL: persist the advanced next-prekey-id counters. Generating
      // prekeys advances nextPreKeyId/nextKyberPreKeyId inside the WASM client,
      // but those counters live only in the identity record. Without re-persisting
      // it, a page reload restores the STALE counter and the next generation
      // reuses the same key_ids — the new private overwrites the old in IDB while
      // the server still serves the old public for that id, so every PQXDH
      // establishment fails with "Operation failed". (Root cause of persistent
      // decrypt failures after a replenish + reload.)
      if (newPreKeys || newKyberKeys || lastResortKyber) {
        await this.persistIdentityRecord();
      }
    } catch (err) {
      console.warn('[E2EE] OTK/kyber replenishment failed:', err);
    }
  }

  // DRH-2 — local self-test of our own freshly built prekey bundle BEFORE we
  // PUT it. A peer establishing a session runs client.process_pre_key_bundle()
  // over whatever we publish; if the bundle is internally inconsistent (signed
  // prekey or kyber signature not produced by our identity key, corrupt/mis-
  // coded public material, b64 round-trip corruption) it surfaces to THEM as the
  // opaque WASM "Operation failed" — and only AFTER the poison is the sole thing
  // peers can fetch. Reproduce that exact verification locally first.
  //
  // Faithfulness: we feed the values decoded from the EXACT b64 strings in the
  // body we are about to PUT (b64Decode(body.signed_prekey_public) etc.), not
  // the raw WASM objects, so an encode/decode corruption is caught too.
  //
  // Safety: purely local. A THROWAWAY, unrelated SignalClient does the verify —
  // no network, no bundle fetch, no prekey of ours is consumed (only our own
  // already-generated PUBLIC material is fed in), and no session is touched.
  // Throws on failure so callers abort the publish rather than serve poison.
  //
  // DRH-2 follow-up (DONE): this pre-publish self-test now guards EVERY publish
  // path — _initIdentityImpl and _linkNewDeviceImpl (full bundles, call this
  // directly) and rotateSignedPrekeyIfNeeded (signed-prekey-only, via
  // selfTestSignedPreKey below) — not just the forceReplacePrekeys self-heal.
  private async selfTestBundle(body: Record<string, unknown>): Promise<void> {
    const otks =
      (body.one_time_prekeys as
        | { key_id: number; public_key: string }[]
        | undefined) ?? [];
    const kybers =
      (body.kyber_prekeys as
        | { key_id: number; public_key: string; signature: string }[]
        | undefined) ?? [];
    // L-10: verify the FIRST OTK/Kyber plus a random sample (up to 3 additional
    // indices) from each batch — checking only index 0 would miss a corrupt key
    // later in the pool. process_pre_key_bundle requires non-empty Kyber
    // material, so a bundle without it is unusable to peers — fail loudly
    // rather than publish it.
    const pickSample = <T>(arr: T[]): T[] => {
      if (arr.length <= 1) return arr.slice(0, 1);
      const idx = new Set<number>([0]);
      const extra = Math.min(3, arr.length - 1);
      while (idx.size < extra + 1) {
        idx.add(1 + Math.floor(Math.random() * (arr.length - 1)));
      }
      return [...idx].sort((a, b) => a - b).map((i) => arr[i]!);
    };
    const otkSamples = pickSample(otks);
    const kyberSamples = pickSample(kybers);
    if (kyberSamples.length === 0) {
      const err = new Error('self_test_no_kyber_prekey');
      (err as Error & { code: string }).code = 'self_test_no_kyber_prekey';
      throw err;
    }

    const tester = new SignalClient('self-test', 1);
    try {
      for (let i = 0; i < kyberSamples.length; i++) {
        const kyber = kyberSamples[i]!;
        const otk =
          otkSamples.length > 0 ? otkSamples[i % otkSamples.length]! : null;
        await wrapWasm(
          'process_pre_key_bundle',
          {
            self_test: true,
            device_id: (this.myDeviceId ?? 1) as number,
            signed_prekey_id: body.signed_prekey_id as number,
            one_time_prekey_id: otk?.key_id ?? undefined,
            kyber_prekey_id: kyber.key_id,
          },
          () =>
            tester.process_pre_key_bundle(
              'self-test-peer',
              (this.myDeviceId ?? 1) as number,
              body.registration_id as number,
              b64Decode(body.identity_key as string),
              body.signed_prekey_id as number,
              b64Decode(body.signed_prekey_public as string),
              b64Decode(body.signed_prekey_signature as string),
              otk ? otk.key_id : null,
              otk ? b64Decode(otk.public_key) : null,
              kyber.key_id,
              b64Decode(kyber.public_key),
              b64Decode(kyber.signature),
            ),
        );
      }
    } finally {
      tester.free();
    }
  }

  // DRH-2 follow-up: pre-publish self-test for the SIGNED-PREKEY-ONLY rotation
  // body. rotateSignedPrekeyIfNeeded PUTs just a signed prekey (no OTK/Kyber),
  // but process_pre_key_bundle — the only WASM verify primitive — REQUIRES Kyber
  // material. Pair the REAL new signed prekey (the exact b64 we are about to PUT)
  // with a THROWAWAY Kyber prekey generated by OUR client, so it is signed under
  // our identity key and the bundle is internally valid, then run the standard
  // selfTestBundle round-trip. This verifies the new signed prekey's signature
  // validates under our identity and that its b64 encoding is clean, BEFORE it
  // replaces the working signed prekey on the server. The filler advances the
  // in-memory Kyber counter by one id (never published/saved) — harmless: saved
  // ids stay below the counter, so DRH-1's integrity check is unaffected.
  private async selfTestSignedPreKey(
    signedBody: Record<string, unknown>,
  ): Promise<void> {
    if (!this.client) return;
    const filler = this.client.generate_kyber_pre_key();
    try {
      await this.selfTestBundle({
        registration_id: signedBody.registration_id,
        identity_key: signedBody.identity_key,
        signed_prekey_id: signedBody.signed_prekey_id,
        signed_prekey_public: signedBody.signed_prekey_public,
        signed_prekey_signature: signedBody.signed_prekey_signature,
        kyber_prekeys: [
          {
            key_id: filler.id,
            public_key: b64Encode(filler.public_key),
            signature: b64Encode(filler.signature),
          },
        ],
      });
    } finally {
      filler.free();
    }
  }

  // Replaces ALL server-side prekey material (OTK + Kyber) with fresh keys
  // while keeping the existing identity key. Used to recover from partial IDB
  // clears where the identity survived but prekey private keys were wiped.
  private async forceReplacePrekeys(): Promise<void> {
    if (!this.client || !this.myDeviceId) return;

    const preKeys = this.client.generate_pre_keys(
      ONE_TIME_PREKEY_COUNT,
    ) as WasmPreKey[];
    const signedPreKey = this.client.generate_signed_pre_key();
    const kyberPreKeys = this.client.generate_kyber_pre_keys(
      KYBER_PREKEY_COUNT,
    ) as WasmKyberPreKey[];
    // Reusable last-resort Kyber prekey — republished on every heal so that a
    // heal (which replaces the whole server kyber pool) never leaves the device
    // without one. Served only when the one-time pool is empty; never consumed.
    const lastResortKyber = this.client.generate_kyber_pre_key();

    const identityPublicKey = this.client.get_identity_public_key();
    const registrationId = this.client.get_registration_id();

    const body: Record<string, unknown> = {
      device_id: this.myDeviceId,
      registration_id: registrationId,
      identity_key: b64Encode(identityPublicKey),
      signed_prekey_id: signedPreKey.id,
      signed_prekey_public: b64Encode(signedPreKey.public_key),
      signed_prekey_signature: b64Encode(signedPreKey.signature),
      one_time_prekeys: preKeys.map((pk) => ({
        key_id: pk.id,
        public_key: b64Encode(pk.public_key),
      })),
      kyber_prekeys: kyberPreKeys.map((pk) => ({
        key_id: pk.id,
        public_key: b64Encode(pk.public_key),
        signature: b64Encode(pk.signature),
      })),
      last_resort_kyber_prekey: {
        key_id: lastResortKyber.id,
        public_key: b64Encode(lastResortKyber.public_key),
        signature: b64Encode(lastResortKyber.signature),
      },
    };

    // DRH-2: verify the bundle locally BEFORE publishing. On failure this throws
    // and we never PUT — the self-heal fails loudly instead of replacing the
    // server material with a bundle peers can't process. Must free the generated
    // WASM keys first to avoid leaking them when we bail out.
    try {
      await this.selfTestBundle(body);
    } catch (err) {
      for (const pk of preKeys) pk.free();
      signedPreKey.free();
      for (const pk of kyberPreKeys) pk.free();
      lastResortKyber.free();
      throw err;
    }

    await this.api('PUT', 'v1/e2ee/identity', { data: body });

    await this.persistIdentityRecord();
    for (const pk of preKeys) {
      await this.store.savePreKey(pk.id, pk.record);
      pk.free();
    }
    await this.store.saveSignedPreKey(signedPreKey.id, signedPreKey.record);
    signedPreKey.free();
    for (const pk of kyberPreKeys) {
      await this.store.saveKyberPreKey(pk.id, pk.record);
      pk.free();
    }
    // Retain the last-resort private forever (never removed; re-imported on every
    // rebuild), so a peer establishing against the reused public always decrypts.
    await this.store.saveKyberPreKey(
      lastResortKyber.id,
      lastResortKyber.record,
    );
    lastResortKyber.free();
  }

  // Public self-heal for prekey public/private DESYNC: the server is serving a
  // bundle whose private half we no longer hold (legacy counter-desync, reused
  // key_id, partial IDB clear), so every inbound PreKeyMessage (type 3)
  // encrypted to it fails with the opaque WASM "Operation failed" — even though
  // our prekey COUNTS look healthy, so checkKeyMaterialHealth never fires.
  //
  // M-7: EVIDENCE-BASED. An undecryptable type-3 is NOT sufficient evidence —
  // a malicious peer (or the server, which owns the mutable `type` column) can
  // fabricate those to trigger a full prekey-pool rewrite (200 OTK + 50 Kyber +
  // signed prekey), invalidating every bundle legitimate peers already fetched.
  // We therefore rotate the pool ONLY when the non-destructive server
  // cross-check (prekey_ids) CONFIRMS an advertised id whose private half we
  // lack; crossCheckServerPrekeyIds performs the heal itself in that case. If
  // the cross-check itself fails (network), nothing is rotated.
  //
  // Rate-limited so a peer spraying undecryptable type-3 messages can't thrash
  // the prekey pool. Does NOT remove any session (safe under the E2EE rules).
  async recoverFromPrekeyDesync(): Promise<void> {
    const now = Date.now();
    if (now - this.lastDesyncRecoveryAt < E2eeCrypto.HEALTH_CHECK_INTERVAL_MS) {
      return;
    }
    // Reserve the full window up-front so a burst of undecryptable type-3s can't
    // trigger a forceReplacePrekeys storm while one attempt is in flight.
    this.lastDesyncRecoveryAt = now;
    try {
      await this._getClient();
      // Returns false only when a real divergence was confirmed AND healed
      // (forceReplacePrekeys ran). True = consistent pool (no rotation needed)
      // or the check was skipped/failed (network — swallowed inside).
      const healed = !(await this.crossCheckServerPrekeyIds());
      if (!healed) {
        // No rotation happened — don't burn the full 30-min window on a
        // non-event; retry after the short backoff instead so a genuine desync
        // surfacing later can still heal promptly.
        this.lastDesyncRecoveryAt =
          now -
          (E2eeCrypto.HEALTH_CHECK_INTERVAL_MS -
            E2eeCrypto.DESYNC_RETRY_BACKOFF_MS);
      }
    } catch (err) {
      // The heal did NOT publish (e.g. the republish PUT /identity was 429'd).
      // Burning the full 30-min window on a FAILED heal strands a wedged device
      // — every inbound type-3 keeps failing with no retry for half an hour.
      // Roll the marker back so the next type-3 re-attempts after a short backoff
      // (not on every message); the full window is only truly spent on a
      // SUCCESSFUL republish. Same "consume the budget only on success" rule as
      // the DRH-4 re-assert loop.
      this.lastDesyncRecoveryAt =
        now -
        (E2eeCrypto.HEALTH_CHECK_INTERVAL_MS -
          E2eeCrypto.DESYNC_RETRY_BACKOFF_MS);
      throw err;
    }
  }

  // DRH-1 — proactive prekey-id counter integrity check. The count-based
  // checkKeyMaterialHealth only fires when a pool hits ZERO, so it is blind to
  // the R47 class: a stale next-id counter (a generate that advanced the WASM
  // counter but never persisted the identity record) survives a reload, and the
  // NEXT generation reuses an id whose public the server still serves — its
  // private half is overwritten in IDB → every future PQXDH establish with that
  // bundle fails with the opaque "Operation failed", with no count anomaly to
  // trip the existing health check.
  //
  // Invariant: every saved (hence published) prekey id MUST be below the WASM
  // next-id counter. If any saved id >= next-id, the counter is stale relative
  // to the material we've already generated → resync proactively, BEFORE we ever
  // serve a poisoned bundle. forceReplacePrekeys rewrites BOTH the server public
  // and the IDB private with the same fresh keypair (and advances + persists the
  // counter), so it resolves the desync even when the counter was stale.
  //
  // Local and zero-cost: no bundle fetch, no prekey burn, no counterparty.
  // Returns true when material is consistent, false when a desync was detected
  // and healed.
  async verifyKeyMaterialIntegrity(): Promise<boolean> {
    if (!this.initialized || !this.client) return true;
    const dump = await this.store.dump();
    const maxSavedId = (prefix: string): number => {
      let max = -1;
      for (const k of Object.keys(dump)) {
        if (!k.startsWith(prefix)) continue;
        const id = Number.parseInt(k.slice(prefix.length), 10);
        if (Number.isInteger(id) && id > max) max = id;
      }
      return max;
    };
    const checks = [
      {
        kind: 'one-time',
        maxSaved: maxSavedId('prekey:'),
        next: this.client.get_next_pre_key_id(),
      },
      {
        kind: 'kyber',
        maxSaved: maxSavedId('kyber-prekey:'),
        next: this.client.get_next_kyber_pre_key_id(),
      },
      {
        kind: 'signed',
        maxSaved: maxSavedId('signed-prekey:'),
        next: this.client.get_next_signed_pre_key_id(),
      },
    ];
    const stale = checks.filter((c) => c.maxSaved >= c.next);
    if (stale.length === 0) return true;
    console.warn(
      '[E2EE] stale prekey-id counter detected (saved id >= next-id) — resyncing prekeys to prevent public/private desync:',
      stale,
    );
    await this.forceReplacePrekeys();
    return false;
  }

  // DRH-1 phase 2 — server-vs-local advertised-id cross-check. The phase-1
  // verifyKeyMaterialIntegrity catches the LOCAL stale-counter signature before
  // a poisoned bundle is served, but it cannot see a desync that already exists
  // on the SERVER: the server may advertise a public prekey id whose PRIVATE
  // half we no longer hold in IDB (partial publish, cross-device rotation, an
  // IDB clear that spared the server pool). A peer then fetches that bundle /
  // signed prekey and we can never decrypt their type-3 establish → silent
  // message loss, with no local count anomaly to trip the existing checks.
  //
  // Fetch the server's advertised ids via the non-destructive prekey_ids
  // endpoint (consumes NO prekey, burns no one-time key), diff against our local
  // PRIVATE records, and heal (forceReplacePrekeys — a PUT-only self-heal that
  // rewrites BOTH the server public and the IDB private with a fresh, self-
  // tested bundle) ONLY when the server advertises an id we lack. The reverse
  // direction (we hold a private id the server has dropped) is harmless
  // (consumed/rotated) and must NOT heal. Any network/api error is swallowed
  // (warn + return true) so this is safe to call opportunistically.
  //
  // Returns true when consistent (or skipped), false when a real divergence was
  // detected and healed.
  async crossCheckServerPrekeyIds(): Promise<boolean> {
    if (!this.initialized || !this.client) return true;
    const deviceId = this.myDeviceId ?? (await this.store.getMyDeviceId());
    if (deviceId === undefined) return true;

    let server: {
      device_id: number;
      signed_prekey_id: number;
      one_time_prekey_ids: number[];
      kyber_prekey_ids: number[];
    };
    try {
      server = await this.api('GET', 'v1/e2ee/identity/prekey_ids', {
        params: { device_id: String(deviceId) },
      });
    } catch (err) {
      console.warn('[E2EE] prekey-id cross-check skipped:', err);
      return true;
    }

    const dump = await this.store.dump();
    const savedIds = (prefix: string): Set<number> => {
      const ids = new Set<number>();
      for (const k of Object.keys(dump)) {
        if (!k.startsWith(prefix)) continue;
        const id = Number.parseInt(k.slice(prefix.length), 10);
        if (Number.isInteger(id)) ids.add(id);
      }
      return ids;
    };
    // 'prekey:' is NOT a prefix of 'kyber-prekey:' or 'signed-prekey:' (those
    // start with 'kyber-' / 'signed-'), so a plain startsWith on each full
    // prefix is unambiguous — kyber-prekey:5 is never counted as one-time id 5.
    const localOtkIds = savedIds('prekey:');
    const localKyberIds = savedIds('kyber-prekey:');
    const localSignedIds = savedIds('signed-prekey:');

    // The DANGEROUS direction: the server advertises a public id whose private
    // half we lack. The reverse (we hold ids the server dropped) is harmless.
    const missingOtks = server.one_time_prekey_ids.filter(
      (id) => !localOtkIds.has(id),
    );
    const missingKyber = server.kyber_prekey_ids.filter(
      (id) => !localKyberIds.has(id),
    );
    const signedMissing = !localSignedIds.has(server.signed_prekey_id);

    if (missingOtks.length || missingKyber.length || signedMissing) {
      console.warn(
        '[E2EE] server advertises prekey ids we cannot decrypt — healing',
        { missingOtks, missingKyber, signedMissing },
      );
      await this.forceReplacePrekeys();
      return false;
    }
    return true;
  }

  // P1 — byte-level published-bundle self-audit. DRH-1 phase 2
  // (crossCheckServerPrekeyIds) compares only prekey IDS; it cannot see a served
  // bundle whose id + private half are valid but whose signature was produced
  // under a PRIOR identity key — the E2EE-HIGH-11 residue (identity_new +
  // kyber_old signed by identity_old). Such a bundle passes every existing
  // client check yet makes process_pre_key_bundle fail ("Operation failed") for
  // EVERY peer establishing to us, silently wedging all inbound sessions.
  //
  // This fetches the exact bundle peers fetch — the new non-destructive,
  // self-scoped GET v1/e2ee/identity/self_bundle (no prekey consumed) — and
  // round-trips it through a throwaway process_pre_key_bundle (reusing the
  // DRH-2 selfTestBundle primitive), exactly what a peer does. On failure it
  // republishes a fresh, self-tested bundle via forceReplacePrekeys (PUT-only;
  // never removeSession), which keeps our CURRENT identity and re-signs fresh
  // kyber under it, so the served bundle becomes peer-verifiable again.
  //
  // A served bundle with NO kyber is depletion, not poison (the count/replenish
  // paths own that), and process_pre_key_bundle requires kyber to verify at all,
  // so we skip rather than heal in that case. Any network/api error is swallowed
  // (warn + return true) so this is safe to call opportunistically.
  //
  // Returns true when the served bundle verifies (or was skipped), false when a
  // real poison was detected and healed.
  async auditPublishedBundle(): Promise<boolean> {
    if (!this.initialized || !this.client) return true;
    const deviceId = this.myDeviceId ?? (await this.store.getMyDeviceId());
    if (deviceId === undefined) return true;

    let served: {
      device_id: number;
      registration_id: number;
      identity_key: string;
      signed_prekey: { key_id: number; public_key: string; signature: string };
      one_time_prekey: { key_id: number; public_key: string } | null;
      kyber_prekey: {
        key_id: number;
        public_key: string;
        signature: string;
      } | null;
    };
    try {
      served = await this.api('GET', 'v1/e2ee/identity/self_bundle', {
        params: { device_id: String(deviceId) },
      });
    } catch (err) {
      console.warn('[E2EE] published-bundle self-audit skipped:', err);
      return true;
    }

    // A depleted kyber pool is NOT poison — replenish/count paths handle it, and
    // process_pre_key_bundle cannot run without kyber material anyway.
    if (!served.kyber_prekey) return true;

    try {
      await this.selfTestBundle({
        registration_id: served.registration_id,
        identity_key: served.identity_key,
        signed_prekey_id: served.signed_prekey.key_id,
        signed_prekey_public: served.signed_prekey.public_key,
        signed_prekey_signature: served.signed_prekey.signature,
        one_time_prekeys: served.one_time_prekey
          ? [served.one_time_prekey]
          : [],
        kyber_prekeys: [served.kyber_prekey],
      });
    } catch (err) {
      console.warn(
        '[E2EE] published bundle fails peer verification — healing (P1 self-audit)',
        err,
      );
      await this.forceReplacePrekeys();
      return false;
    }
    return true;
  }

  // Called on user-initiated actions (opening a conversation, sending a message).
  // Debounced to at most once per HEALTH_CHECK_INTERVAL_MS. If either OTK or
  // Kyber prekey private keys are absent from IDB (mid-session clear by the
  // browser's storage eviction or user action), regenerates and replaces all
  // prekey material on the server.
  async checkKeyMaterialHealth(): Promise<void> {
    if (!this.initialized || !this.client) return;
    const now = Date.now();
    if (now - this.lastHealthCheckAt < E2eeCrypto.HEALTH_CHECK_INTERVAL_MS)
      return;
    this.lastHealthCheckAt = now;

    // DRH-1: proactive counter-integrity check first — catches the R47
    // stale-counter desync class that the count-based logic below is blind to,
    // and heals it (resync) before any peer fetches a poisoned bundle.
    const integrityOk = await this.verifyKeyMaterialIntegrity();

    // DRH-1 phase 2: cross-check the server's advertised prekey ids against our
    // local private records — catches a server-side public/private desync the
    // purely-local integrity check cannot see. Skip if phase 1 already healed
    // (it just rewrote both sides), to avoid a redundant second PUT. Safe to
    // call opportunistically: any network error → warn + return true.
    if (integrityOk) {
      const idsOk = await this.crossCheckServerPrekeyIds();

      // P1 phase 3: byte-level audit of the bundle peers actually fetch —
      // catches a served bundle whose ids are all valid but whose signatures
      // were produced under a PRIOR identity (the E2EE-HIGH-11 residue), which
      // the id-only cross-check above is blind to. Skip if the cross-check just
      // healed (it already republished a fresh, self-tested bundle).
      if (idsOk) {
        await this.auditPublishedBundle();
      }
    }

    const dump = await this.store.dump();
    const keys = Object.keys(dump);
    const kyberCount = keys.filter((k) => k.startsWith('kyber-prekey:')).length;
    const otkCount = keys.filter((k) => k.startsWith('prekey:')).length;

    if (kyberCount === 0 || otkCount === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        '[E2EE] IDB prekey material missing mid-session (kyber=',
        kyberCount,
        'otk=',
        otkCount,
        ') — replacing server prekeys',
      );
      await this.forceReplacePrekeys();
    } else {
      // IDB private keys are intact — also verify the server still has public
      // prekeys available. Auto-recovery bursts (multi-tab or retry storms) can
      // exhaust the server pool even when IDB is healthy.
      await this.replenishOneTimePrekeysIfNeeded();
    }
  }

  private async rotateSignedPrekeyIfNeeded(): Promise<void> {
    if (!this.client || !this.myDeviceId) return;

    try {
      const lastRotationRaw = await this.store.load<number>(
        'signed-prekey-rotated-at',
      );
      const now = Date.now();
      const jitterMs = Math.floor(Math.random() * 6 * 60 * 60 * 1000);
      const rotationIntervalMs = 24 * 60 * 60 * 1000 + jitterMs;

      if (lastRotationRaw && now - lastRotationRaw < rotationIntervalMs) {
        return;
      }

      const oldSignedPreKeyId = this.client.get_next_signed_pre_key_id() - 1;
      const newSignedPreKey = this.client.generate_signed_pre_key();

      const identityPublicKey = this.client.get_identity_public_key();
      const registrationId = this.client.get_registration_id();

      const body: Record<string, unknown> = {
        registration_id: registrationId,
        identity_key: b64Encode(identityPublicKey),
        signed_prekey_id: newSignedPreKey.id,
        signed_prekey_public: b64Encode(newSignedPreKey.public_key),
        signed_prekey_signature: b64Encode(newSignedPreKey.signature),
      };
      body.device_id = this.myDeviceId;

      // DRH-2: verify the new signed prekey validates under our identity and its
      // b64 is clean BEFORE it replaces the working signed prekey on the server.
      // On failure, free the key and rethrow — the surrounding catch turns it
      // into a warn + skipped rotation, keeping the current known-good key.
      try {
        await this.selfTestSignedPreKey(body);
      } catch (err) {
        newSignedPreKey.free();
        throw err;
      }

      await this.api('PUT', 'v1/e2ee/identity', { data: body });

      await this.store.saveSignedPreKey(
        newSignedPreKey.id,
        newSignedPreKey.record,
      );
      newSignedPreKey.free();

      // Persist the advanced nextSignedPreKeyId counter (see the replenish path):
      // without this, a reload restores the stale counter and the next rotation
      // reuses the same signed_prekey_id, desyncing server public vs local private.
      await this.persistIdentityRecord();

      if (oldSignedPreKeyId >= 0) {
        await this.store.save(`signed-prekey-grace:${oldSignedPreKeyId}`, {
          id: oldSignedPreKeyId,
          expires_at: now + 48 * 60 * 60 * 1000,
        });
      }

      await this.store.save('signed-prekey-rotated-at', now);

      await this.cleanupExpiredSignedPrekeys();
    } catch (err) {
      console.warn('[E2EE] Signed prekey rotation failed:', err);
    }
  }

  private async cleanupExpiredSignedPrekeys(): Promise<void> {
    const now = Date.now();
    const keys = await this.store.dump();
    for (const [key, value] of Object.entries(keys)) {
      if (key.startsWith('signed-prekey-grace:')) {
        const graceData = value as { id: number; expires_at: number };
        if (
          graceData &&
          typeof graceData.expires_at === 'number' &&
          now >= graceData.expires_at
        ) {
          await this.store.remove(key);
          await this.store.remove(`signed-prekey:${graceData.id}`);
        }
      }
    }
  }

  async decryptMessage(
    senderAccountId: string,
    ciphertextB64: string,
    type: number,
    senderDeviceId = 1,
  ): Promise<PlaintextPayload> {
    return this.withWriterLock(() =>
      this.decryptMessageLocked(
        senderAccountId,
        ciphertextB64,
        type,
        senderDeviceId,
      ),
    );
  }

  private async decryptMessageLocked(
    senderAccountId: string,
    ciphertextB64: string,
    type: number,
    senderDeviceId = 1,
  ): Promise<PlaintextPayload> {
    const client = await this._getClient();
    const ciphertext = b64Decode(ciphertextB64);
    // M-7 / L-8: the `type` column is server-stored and server-mutable. A
    // flipped type steers our failure handling (2→3 forces parse failure →
    // pool rotation; 3→2 triggers session archival). Sanity-check the stored
    // type against the ciphertext's own format: when the sniffer is confident
    // and disagrees, distrust the stored type and derive the effective type
    // from the ciphertext. When the sniffer is unsure (null), the stored type
    // governs as before. This guarantees pool rotation / session archival are
    // never triggered by a stored type that contradicts the ciphertext.
    const sniffedType = sniffSignalMessageType(ciphertext);
    let effectiveType = type;
    if (sniffedType !== null && sniffedType !== type) {
      console.warn(
        '[e2ee] server-stored ciphertext_type contradicts the ciphertext format — distrusting the stored type',
        {
          storedType: type,
          sniffedType,
          peer: senderAccountId,
          senderDeviceId,
        },
      );
      effectiveType = sniffedType;
    }
    let plaintext: Uint8Array;
    try {
      plaintext = await wrapWasm(
        'decrypt_message',
        {
          peer: senderAccountId,
          device_id: senderDeviceId,
          type: effectiveType,
          stored_type: type,
          ct_bytes: ciphertext.length,
        },
        () =>
          client.decrypt_message(
            senderAccountId,
            senderDeviceId,
            ciphertext,
            effectiveType,
          ),
      );
    } catch (err) {
      // For WhisperMessages, archive the stale session in the WASM client so
      // the sender's next outbound encrypt triggers fresh X3DH (PreKeyMessage).
      // Do NOT remove the session from IDB — deleting it causes permanent data
      // loss: on the next page load the WASM restores without it and every
      // un-cached message under that session fails forever.
      if (effectiveType === 2) {
        try {
          await client.archive_session(senderAccountId, senderDeviceId);
          // LOW-9 (resolved BY-DESIGN, not persisted): we deliberately do NOT
          // export_session + saveSession the archived record here. Persisting it
          // makes a later client rebuild (syncClientToEpoch) restore an ARCHIVED
          // session, which has no sending chain — the sender's next encrypt then
          // throws "Operation failed" (has_session is true, so the send path does
          // not re-establish). Keeping the archive in-memory only means a rebuild
          // restores the still-valid CURRENT session (the sender keeps working) and
          // re-establishment is driven by the resend/recovery flow — without
          // bricking the send path or burning a prekey on a benign replay. See
          // e2ee_session_edge_cases "type=2 archive keeps the send path working
          // after a rebuild" and PLATFORM_SECURITY_REVIEW E2EE-LOW-9.
        } catch {
          /* best-effort */
        }
      }
      // For PreKeyMessages (type 3), a failure means the sender used a prekey
      // bundle whose private half we no longer hold — a prekey public/private
      // DESYNC. Our prekey COUNTS may still look healthy, so the periodic
      // health check never fires and we'd fail FOREVER. Self-heal (M-7:
      // evidence-based — the pool is rotated only when the server cross-check
      // confirms a desync), so the sender's next establish (driven by our
      // resend_request → forceReestablish) uses a bundle we can decrypt.
      // Rate-limited and non-destructive (no session removal); fire-and-forget
      // so we still surface the failure for the resend flow. The current
      // message is recovered via that resend.
      if (effectiveType === 3) {
        void this.recoverFromPrekeyDesync().catch(() => {
          /* best-effort self-heal */
        });
      }
      throw err;
    }
    const sessionBytes = await client.export_session(
      senderAccountId,
      senderDeviceId,
    );
    if (sessionBytes) {
      await this.store.saveSession(
        senderAccountId,
        senderDeviceId,
        sessionBytes,
      );
    }
    await this.store.touchSessionTimestamp(
      senderAccountId,
      senderDeviceId,
      'recv',
    );
    return JSON.parse(utf8Decode(plaintext)) as PlaintextPayload;
  }

  async encryptBytes(plain: ArrayBuffer): Promise<EncryptedBytes> {
    const keyRaw = crypto.getRandomValues(new Uint8Array(32));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await crypto.subtle.importKey('raw', keyRaw, 'AES-GCM', false, [
      'encrypt',
    ]);
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain);
    return {
      bytes: ct,
      key: b64Encode(keyRaw),
      iv: b64Encode(iv),
    };
  }

  async decryptBytes(
    encrypted: ArrayBuffer,
    keyB64: string,
    ivB64: string,
  ): Promise<ArrayBuffer> {
    const key = await crypto.subtle.importKey(
      'raw',
      b64Decode(keyB64) as unknown as BufferSource,
      'AES-GCM',
      false,
      ['decrypt'],
    );
    return crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64Decode(ivB64) as unknown as BufferSource },
      key,
      encrypted,
    );
  }

  async rememberSentMessage(
    messageId: string,
    payload: PlaintextPayload,
  ): Promise<void> {
    await this.store.storeSentPlaintext(messageId, payload);
  }

  async recallSentMessage(
    messageId: string,
  ): Promise<PlaintextPayload | undefined> {
    return this.store.loadSentPlaintext<PlaintextPayload>(messageId);
  }

  async rememberInboundMessage(
    messageId: string,
    payload: PlaintextPayload,
  ): Promise<void> {
    await this.store.storeInboundPlaintext(messageId, payload);
  }

  async recallInboundMessage(
    messageId: string,
  ): Promise<PlaintextPayload | undefined> {
    return this.store.loadInboundPlaintext<PlaintextPayload>(messageId);
  }

  // DRH-5: durable resend-replacement cache, keyed by the FAILED message's
  // logical id (group_key ?? id). Lets a previously-undecryptable bubble resolve
  // from the resent plaintext after a reload, when its own ciphertext can no
  // longer be ratchet-decrypted.
  async rememberReplacementMessage(
    failedKey: string,
    payload: PlaintextPayload,
  ): Promise<void> {
    await this.store.storeReplacementPlaintext(failedKey, payload);
  }

  async recallReplacementMessage(
    failedKey: string,
  ): Promise<PlaintextPayload | undefined> {
    return this.store.loadReplacementPlaintext<PlaintextPayload>(failedKey);
  }

  // DRH-4: per-conversation convergence metadata (message ids only, no plaintext
  // or keys). Sender remembers the peer's highest ack; recipient remembers the
  // last ack it sent (throttle). Local-only; excluded from backup, wiped on
  // destroy. See E2EE_SECURITY.md §13.7 DRH-4.
  async rememberPeerAck(peerAccountId: string, ackId: string): Promise<void> {
    await this.store.storePeerAck(peerAccountId, ackId);
  }

  async recallPeerAck(peerAccountId: string): Promise<string | undefined> {
    return this.store.loadPeerAck(peerAccountId);
  }

  async rememberSentAck(peerAccountId: string, ackId: string): Promise<void> {
    await this.store.storeSentAck(peerAccountId, ackId);
  }

  async recallSentAck(peerAccountId: string): Promise<string | undefined> {
    return this.store.loadSentAck(peerAccountId);
  }

  async uploadBackup(passphrase: string): Promise<void> {
    const { encryptBackupV2 } = await import('./backup');
    const snapshot = await this.store.dump();
    const envelope = await encryptBackupV2(snapshot, passphrase);
    await this.api('PUT', 'v1/e2ee/backup', {
      data: {
        ciphertext: b64Encode(envelope.ciphertext),
        kdf_salt: envelope.salt,
        kdf_params: envelope.kdfParams,
      },
    });
  }

  async restoreBackup(passphrase: string): Promise<void> {
    const resp = await this.api<{
      ciphertext: string;
      kdf_salt: string;
      kdf_params: Record<string, unknown>;
    }>('GET', 'v1/e2ee/backup');

    const ct = b64Decode(resp.ciphertext);

    // L-1: the old minimum-params check (m/t floors) was dead code — the backup
    // KDF always runs with hardcoded Argon2id constants (deriveBackupKey ignores
    // server-supplied params by design, so no KDF downgrade is possible). What
    // MUST still be enforced is the format itself: only argon2id (v2) backups
    // are decryptable; v1/unknown records are rejected with a typed error.
    if (resp.kdf_params?.algorithm !== 'argon2id') {
      const err = new Error(
        'Server-stored backup uses an unsupported format (v1 or unknown KDF). ' +
          'Please re-set-up backup from a device that has your current keys.',
      );
      (err as Error & { code: string }).code = 'backup_unsupported_kdf';
      throw err;
    }

    const { decryptBackupV2 } = await import('./backup');
    const snapshot = await decryptBackupV2(
      ct.buffer.slice(ct.byteOffset, ct.byteOffset + ct.byteLength),
      passphrase,
      resp.kdf_salt,
      resp.kdf_params as {
        algorithm: 'argon2id';
        m: number;
        t: number;
        p: number;
      },
    );

    await this.store.hydrate(snapshot);
    // Clear the WASM client so the next call re-creates it from the newly
    // hydrated store rather than continuing to use the old identity (N12 fix).
    this.client = undefined;
    this.initialized = false;
  }

  async hasRemoteBackup(): Promise<boolean> {
    // Ticket 57: asks the dedicated existence endpoint, which answers
    // `200 {"exists": false}` rather than 404. The download route still 404s
    // — that is the right answer to "give me the backup" — but it made every
    // first-time login post an error into the staff dashboard tail.
    //
    // M-4 is unchanged and is the reason this reads the way it does:
    // fail-closed. ONLY an explicit boolean counts as an answer. Any error
    // (5xx, offline, timeout, a server that does not have this route and so
    // 404s it) propagates, because treating a transient failure as "no
    // backup" lets it — or a malicious server answering selectively — steer
    // the setup flow into re-keying the account and destroying the backup
    // that was there all along.
    const resp = await this.api<{ exists?: unknown }>(
      'GET',
      'v1/e2ee/backup/status',
    );
    if (typeof resp?.exists !== 'boolean') {
      throw new Error('Malformed backup status response');
    }
    return resp.exists;
  }

  // M-12: PUT the account backup. `opts.ifMatchUpdatedAt` sends the
  // last-seen backup `updated_at` as a write precondition — the server
  // answers 409 when ANOTHER device owns a newer backup. The 409 is thrown
  // as a typed error (`code === 'e2ee_backup_conflict'`, with the server's
  // current `updated_at` on `.updatedAt`) so the auto-backup scheduler can
  // stand down instead of thrashing last-writer-wins. A manual setupBackup
  // passes NO precondition (force-overwrite). Returns the server's
  // `updated_at` for the new backup when present.
  async uploadBackupWithKey(
    rawKeyBytes: ArrayBuffer,
    saltB64: string,
    opts?: { ifMatchUpdatedAt?: string },
  ): Promise<string | undefined> {
    const {
      encryptBackupV2,
      ARGON2_MEMORY_KIB,
      ARGON2_ITERATIONS,
      ARGON2_PARALLELISM,
    } = await import('./backup');
    const snapshot = await this.store.dump();
    const envelope = await encryptBackupV2(snapshot, '', rawKeyBytes);

    // Always send kdf_params so the server record stays consistent.
    // Omitting them causes the controller to store {} and break passphrase-based restore.
    try {
      const resp = await this.api<{ updated_at?: string }>(
        'PUT',
        'v1/e2ee/backup',
        {
          data: {
            ciphertext: b64Encode(envelope.ciphertext),
            kdf_salt: saltB64,
            kdf_params: {
              algorithm: 'argon2id',
              m: ARGON2_MEMORY_KIB,
              t: ARGON2_ITERATIONS,
              p: ARGON2_PARALLELISM,
            },
            ...(opts?.ifMatchUpdatedAt
              ? { if_match_updated_at: opts.ifMatchUpdatedAt }
              : {}),
          },
        },
      );
      return typeof resp?.updated_at === 'string' ? resp.updated_at : undefined;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) {
        const body = (err as { response?: { data?: { updated_at?: unknown } } })
          .response?.data;
        const conflict = new Error('e2ee_backup_conflict') as Error & {
          code?: string;
          updatedAt?: string;
        };
        conflict.code = 'e2ee_backup_conflict';
        if (typeof body?.updated_at === 'string')
          conflict.updatedAt = body.updated_at;
        throw conflict;
      }
      throw err;
    }
  }

  async destroy(): Promise<void> {
    // Use destroyAll() instead of dump() — dump() intentionally excludes
    // sent:*/recv:* entries from backups, but destroy must clear everything.
    // (Deleting the whole IDB database races in-page re-initialization — a
    // pending delete blocks the next open() on the same name. Key-level wipe
    // is the C8 requirement; the empty shell is harmless.)
    await this.store.destroyAll();
    this.client = undefined;
    this.initialized = false;
  }

  async expireSessions(): Promise<number> {
    const result = await this.runSessionCleanup();
    return result.expired;
  }

  async enforceSessionLimit(): Promise<number> {
    const result = await this.runSessionCleanup();
    return result.evicted;
  }

  private async runSessionCleanup(): Promise<SessionCleanupResult> {
    // MED-16: run under the writer lock so cleanup's archive_session +
    // removeSession cannot race a concurrent locked op's syncClientToEpoch
    // (which could drop + rebuild the client mid-eviction). withWriterLock also
    // reloads-before-op and bumps the epoch after, so sibling tabs reload and
    // don't keep a session this cleanup just evicted.
    return this.withWriterLock(() => this.runSessionCleanupLocked());
  }

  private async runSessionCleanupLocked(): Promise<SessionCleanupResult> {
    const client = await this._getClient();
    const sessions = await this.store.listSessionKeys();
    const now = Date.now();
    let expired = 0;
    let evicted = 0;

    // First pass: remove expired sessions (TTL-based)
    for (const { accountId, deviceId } of sessions) {
      const ts = await this.store.loadSessionTimestamp(accountId, deviceId);
      const lastSend = ts?.lastSend ?? 0;
      const lastRecv = ts?.lastRecv ?? 0;
      if (
        now - lastSend > SESSION_MAX_SEND_MS &&
        now - lastRecv > SESSION_MAX_RECV_MS
      ) {
        try {
          await client.archive_session(accountId, deviceId);
        } catch {
          // session may not be in WASM memory
        }
        // eslint-disable-next-line no-restricted-syntax -- PERMITTED: removeSession in TTL expiry (LRU/TTL eviction path)
        await this.store.removeSession(accountId, deviceId);
        expired++;
      }
    }

    // Second pass: enforce max count (LRU eviction by last activity)
    const activeKeys = await this.store.listSessionKeys();
    if (activeKeys.length > SESSION_MAX_COUNT) {
      const scored: ScoredSession[] = [];
      for (const { accountId, deviceId } of activeKeys) {
        const ts = await this.store.loadSessionTimestamp(accountId, deviceId);
        scored.push({
          accountId,
          deviceId,
          lastActivity: Math.max(ts?.lastSend ?? 0, ts?.lastRecv ?? 0),
        });
      }
      scored.sort((a, b) => a.lastActivity - b.lastActivity);
      while (scored.length > SESSION_MAX_COUNT) {
        const oldest = scored.shift()!;
        try {
          await client.archive_session(oldest.accountId, oldest.deviceId);
        } catch {}
        // eslint-disable-next-line no-restricted-syntax -- PERMITTED: removeSession in LRU eviction (LRU/TTL eviction path)
        await this.store.removeSession(oldest.accountId, oldest.deviceId);
        evicted++;
      }
    }

    // M-1: piggyback the plaintext-cache retention sweep (TTL + LRU cap over
    // sent:/recv:/replace: and the sibling-backfill outbox) on this periodic
    // cleanup pass. Best-effort: retention failure must not break cleanup.
    try {
      await this.store.sweepPlaintextCaches();
    } catch {
      /* non-fatal */
    }

    return { expired, evicted };
  }

  // --- Safety Number — identity verification (closes R1 MITM gap) ---

  // H-4: list every device of a contact (identity keys as currently served).
  // The safety-number UI enumerates devices with this so verification covers
  // all of them, not just device 1.
  async listContactDevices(
    contactAccountId: string,
  ): Promise<Array<{ device_id: number; identity_key: string }>> {
    return this.fetchIdentityOnly(contactAccountId);
  }

  // Resolve the identity key to verify against for (contact, deviceId):
  // prefer the locally pinned key (what sessions were established to); fall
  // back to the currently-served key for that device (and pin it).
  private async resolveIdentityKeyForDevice(
    contactAccountId: string,
    deviceId: number,
  ): Promise<string | undefined> {
    const pinned = await this.store.loadRemoteIdentityKey(
      contactAccountId,
      deviceId,
    );
    if (pinned) return pinned;
    const identityDevices = await this.fetchIdentityOnly(contactAccountId);
    const identityDevice =
      identityDevices.find((d) => d.device_id === deviceId) ??
      (deviceId === 1 ? identityDevices[0] : undefined);
    if (!identityDevice) return undefined;
    await this.store.saveRemoteIdentityKey(
      contactAccountId,
      identityDevice.identity_key,
      deviceId,
    );
    return identityDevice.identity_key;
  }

  async generateSafetyNumber(
    contactAccountId: string,
    deviceId = 1,
  ): Promise<SafetyNumber> {
    const client = await this._getClient();
    const identityKey = await this.resolveIdentityKeyForDevice(
      contactAccountId,
      deviceId,
    );
    if (!identityKey) {
      throw new Error('No devices found for this contact');
    }
    const sn = client.generate_safety_number(
      contactAccountId,
      b64Decode(identityKey),
    );
    try {
      return {
        displayable: sn.displayable,
        scannable: sn.scannable.buffer.slice(
          sn.scannable.byteOffset,
          sn.scannable.byteOffset + sn.scannable.byteLength,
        ),
      };
    } finally {
      sn.free();
    }
  }

  async verifySafetyNumber(
    contactAccountId: string,
    scanned: Uint8Array,
    deviceId = 1,
  ): Promise<boolean> {
    const client = await this._getClient();
    const identityKey = await this.resolveIdentityKeyForDevice(
      contactAccountId,
      deviceId,
    );
    if (!identityKey) return false;
    return client.verify_safety_number(
      scanned,
      contactAccountId,
      b64Decode(identityKey),
    );
  }

  // H-4/H-5: check EVERY served device of the contact against its stored
  // per-device pin (not just device 1), and detect first-seen devices of an
  // already-known contact. On a detected key change the verification state
  // for that device is reset (a "verified" badge bound to the OLD key must
  // never survive onto the new one).
  async checkIdentityKeyChanged(
    contactAccountId: string,
  ): Promise<IdentityKeyChange> {
    const devices = await this.fetchIdentityOnly(contactAccountId);
    // H-5: new-device detection against the last-seen set. The seen set is
    // NOT updated here — it advances only on first contact (TOFU, in the
    // send path) and on explicit user acknowledgment, so an unacknowledged
    // new device keeps surfacing until the user acts.
    const seen = await this.store.loadSeenDeviceIds(contactAccountId);
    const newDevices =
      seen.length > 0
        ? devices
            .map((d) => d.device_id)
            .filter((id) => !seen.includes(id))
            .sort((a, b) => a - b)
        : [];
    for (const device of devices) {
      const stored = await this.store.loadRemoteIdentityKey(
        contactAccountId,
        device.device_id,
      );
      if (stored && stored !== device.identity_key) {
        await this.store.resetSafetyNumberVerified(
          contactAccountId,
          device.device_id,
        );
        return {
          changed: true,
          oldKey: stored,
          newKey: device.identity_key,
          deviceId: device.device_id,
          ...(newDevices.length > 0 ? { newDevices } : {}),
        };
      }
    }
    return {
      changed: false,
      ...(newDevices.length > 0 ? { newDevices } : {}),
    };
  }

  private async fetchIdentityOnly(
    accountId: string,
  ): Promise<Array<{ device_id: number; identity_key: string }>> {
    const resp = await this.api<{
      account_id: string;
      devices: Array<{ device_id: number; identity_key: string }>;
    }>('GET', `v1/e2ee/identity/${accountId}/identity_key`);
    return resp.devices;
  }

  // === Group E2EE (Sender Key) ===

  /**
   * Create a Sender Key Distribution Message (SKDM) for a group.
   * This must be shared with every member so they can decrypt messages
   * from this sender. Returns the raw SKDM bytes as base64.
   */
  async createSenderKeyDistribution(distributionId: string): Promise<string> {
    const client = await this._getClient();
    const accountId = this.myAccountId ?? 'unknown';
    const deviceId = this.myDeviceId ?? 1;

    // Always distribute the initial (iteration-0) SKDM bytes.
    // create_sender_key_distribution returns the CURRENT chain state (iteration N
    // after N messages sent). Receivers given an iteration-N SKDM cannot decrypt
    // messages at iterations 0..N-1 (one-way hash ratchet). We save the first
    // call's bytes and reuse them on all subsequent distributions so every
    // receiver starts at iteration 0 and can decrypt all history.
    const existingInitBytes = await this.store.loadInitialSkdmBytes(
      accountId,
      deviceId,
      distributionId,
    );
    if (existingInitBytes) {
      if (process.env.NODE_ENV !== 'production') {
        const iter = parseSignalProtobufVarint(existingInitBytes, 3);
        console.debug(
          '[E2EE][create-skdm] reusing stored initial bytes',
          'distId=',
          distributionId,
          'len=',
          existingInitBytes.length,
          'skdmIteration=',
          iter,
          '← iteration-0 fix active',
        );
      }
      return b64Encode(existingInitBytes);
    }

    const skdmBytes =
      await client.create_sender_key_distribution(distributionId);
    if (process.env.NODE_ENV !== 'production') {
      const iter = parseSignalProtobufVarint(skdmBytes, 3);
      console.debug(
        '[E2EE][create-skdm] fresh WASM bytes (first time)',
        'distId=',
        distributionId,
        'len=',
        skdmBytes.length,
        'skdmIteration=',
        iter,
        'hex=',
        hexDump(skdmBytes, 16),
      );
    }

    // Save as the initial bytes — all future distributions for this distId reuse them.
    await this.store.saveInitialSkdmBytes(
      accountId,
      deviceId,
      distributionId,
      skdmBytes,
    );

    // Persist our own sender key locally (restores encrypt capability on page reload).
    const exported = await client.export_sender_key(
      accountId,
      deviceId,
      distributionId,
    );
    if (exported) {
      await this.store.saveSenderKey(
        accountId,
        deviceId,
        distributionId,
        exported,
      );
    }
    return b64Encode(skdmBytes);
  }

  /**
   * Process a received SKDM from another group member so we can
   * decrypt their future group messages. Persists the learned sender
   * key to IndexedDB so it survives page refresh (R18 fix).
   */
  async processSenderKeyDistribution(
    senderAccountId: string,
    senderDeviceId: number,
    distributionId: string,
    distributionMessageB64: string,
  ): Promise<void> {
    const client = await this._getClient();
    const skdm = b64Decode(distributionMessageB64);
    await client.process_sender_key_distribution(
      senderAccountId,
      senderDeviceId,
      skdm,
    );
    // Persist the learned sender key to IndexedDB
    const exported = await client.export_sender_key(
      senderAccountId,
      senderDeviceId,
      distributionId,
    );
    if (exported) {
      await this.store.saveSenderKey(
        senderAccountId,
        senderDeviceId,
        distributionId,
        exported,
      );
    }
  }

  /**
   * Encrypt a group message using Sender Key. The message is encrypted
   * once and can be decrypted by all members who have processed our SKDM.
   */
  async encryptGroupMessage(
    distributionId: string,
    payload: PlaintextPayload,
  ): Promise<GroupCipherOutput> {
    return this.withWriterLock(() =>
      this.encryptGroupMessageLocked(distributionId, payload),
    );
  }

  private async encryptGroupMessageLocked(
    distributionId: string,
    payload: PlaintextPayload,
  ): Promise<GroupCipherOutput> {
    const client = await this._getClient();
    const buf = utf8Encode(JSON.stringify(payload));
    const ct = await wrapWasm(
      'encrypt_group_message',
      { distribution_id: distributionId, plaintext_bytes: buf.length },
      () => client.encrypt_group_message(distributionId, buf),
    );
    // Persist the ADVANCED sender chain (DRH-3). encrypt_group_message steps the
    // in-memory sender-key hash ratchet; unlike the DM double ratchet we must
    // export+save it here, because a sibling tab's epoch bump can drop this
    // client (syncClientToEpoch) and rebuild it from IDB — where only the
    // iteration-0 state saved at createSenderKeyDistribution lives. Without this,
    // the rebuilt chain rewinds to 0 and re-emits low-iteration ciphertext that
    // receivers (one-way ratchet already ahead) silently drop → group message
    // loss / keystream reuse.
    const accountId = this.myAccountId ?? 'unknown';
    const deviceId = this.myDeviceId ?? 1;
    const exported = await client.export_sender_key(
      accountId,
      deviceId,
      distributionId,
    );
    if (exported) {
      await this.store.saveSenderKey(
        accountId,
        deviceId,
        distributionId,
        exported,
      );
    }
    return {
      ciphertextB64: b64Encode(ct),
      type: 5, // message_type_sender_key
    };
  }

  /**
   * Decrypt a group message from a specific sender.
   *
   * M-2: `expectedDistributionId` (the thread's CURRENT server-side
   * distribution_id) is REQUIRED. Before ANY processing — including the
   * cold-chain reset, which parses the distId from untrusted ciphertext
   * bytes — the distId embedded in the SenderKeyMessage header is extracted
   * and the ciphertext is REJECTED with an E2eeError
   * (`context.code === 'group_distid_mismatch'`) unless it matches. This binds
   * group ciphertexts to their group/epoch: a member (or the server) can no
   * longer repost a valid ciphertext from group B into group A, or from an
   * old epoch into the current one, and have it render as a local message.
   *
   * Fast path: try decrypt_group_message directly (succeeds when
   * fetchAndProcessSenderKeys already ran for this sender).
   *
   * Cold-chain reset: on first failure, load stored SKDM bytes from IDB and
   * call process_sender_key_distribution to initialize the receive chain.
   * Then retry. This covers streaming messages that arrive before
   * fetchAndProcessSenderKeys has run.
   */
  async decryptGroupMessage(
    senderAccountId: string,
    senderDeviceId: number,
    ciphertextB64: string,
    expectedDistributionId: string,
  ): Promise<GroupDecryptResult> {
    return this.withWriterLock(() =>
      this.decryptGroupMessageLocked(
        senderAccountId,
        senderDeviceId,
        ciphertextB64,
        expectedDistributionId,
      ),
    );
  }

  private async decryptGroupMessageLocked(
    senderAccountId: string,
    senderDeviceId: number,
    ciphertextB64: string,
    expectedDistributionId: string,
  ): Promise<GroupDecryptResult> {
    const client = await this._getClient();
    const ciphertext = b64Decode(ciphertextB64);

    // M-2: bind the ciphertext to the expected group/epoch BEFORE anything
    // else. The embedded distId is attacker-influenceable, so it must be
    // checked against the caller-supplied (thread) value, never trusted.
    if (!isSkmHeader(ciphertext)) {
      throw new E2eeError(
        'decrypt_group_message',
        {
          code: 'group_distid_mismatch',
          sender: senderAccountId,
          sender_device_id: senderDeviceId,
          expected_dist_id: expectedDistributionId,
          actual_dist_id: null,
        },
        new Error(
          'group ciphertext is not a recognizable SenderKeyMessage — cannot verify its distribution id',
        ),
      );
    }
    const distIdFromCt = bytesToUuid(ciphertext.slice(3, 19));
    if (distIdFromCt !== expectedDistributionId.toLowerCase()) {
      throw new E2eeError(
        'decrypt_group_message',
        {
          code: 'group_distid_mismatch',
          sender: senderAccountId,
          sender_device_id: senderDeviceId,
          expected_dist_id: expectedDistributionId,
          actual_dist_id: distIdFromCt,
        },
        new Error(
          `group ciphertext distribution id ${distIdFromCt} does not match expected ${expectedDistributionId} — rejected (cross-group/cross-epoch confusion)`,
        ),
      );
    }
    // Sender-chain iteration (field 3) for replay dedupe by the caller (L-12).
    const iteration = bigintToSafeNumber(
      parseSignalProtobufVarint(ciphertext, 3),
    );

    if (process.env.NODE_ENV !== 'production') {
      console.debug(
        '[E2EE][decrypt-group] sender=',
        senderAccountId,
        'device=',
        senderDeviceId,
        'ctLen=',
        ciphertext.length,
        'msgIteration=',
        iteration,
        'distId=',
        distIdFromCt,
        'ct[0..16]=',
        hexDump(ciphertext, 16),
      );
      try {
        const keyCheck = await client.export_sender_key(
          senderAccountId,
          senderDeviceId,
          distIdFromCt,
        );
        console.debug(
          '[E2EE][decrypt-group] distIdFromCt=',
          distIdFromCt,
          'export_sender_key=',
          keyCheck ? `${keyCheck.length} bytes ✓` : 'NULL ← KEY MISSING',
        );
      } catch (e) {
        console.debug('[E2EE][decrypt-group] export_sender_key threw:', e);
      }
    }

    // Fast path: chain already initialized by fetchAndProcessSenderKeys.
    try {
      const plaintext = await client.decrypt_group_message(
        senderAccountId,
        senderDeviceId,
        ciphertext,
      );
      return {
        ...(JSON.parse(utf8Decode(plaintext)) as PlaintextPayload),
        iteration,
      };
    } catch (fastPathErr) {
      // Fall through to cold-chain reset. Log the original error (dev only):
      // any failure — not just "chain not initialized" — lands here, and the
      // cold-chain retry below only surfaces ITS error, so without this the
      // real cause is invisible when diagnosing "[unable to decrypt]" reports.
      if (process.env.NODE_ENV !== 'production') {
        console.debug(
          '[E2EE][decrypt-group] fast path failed, attempting cold-chain reset:',
          fastPathErr,
        );
      }
    }

    // Cold-chain reset: re-process the stored SKDM bytes for this (verified)
    // distribution id to initialize the receive chain.
    const storedBytes = await this.store.loadSkdmBytes(
      senderAccountId,
      senderDeviceId,
      distIdFromCt,
    );
    if (storedBytes) {
      try {
        await client.process_sender_key_distribution(
          senderAccountId,
          senderDeviceId,
          storedBytes,
        );
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug(
            '[E2EE][decrypt-group] process_sender_key_distribution threw during cold-chain reset:',
            e,
          );
        }
      }
    }

    // Retry after cold-chain reset.
    try {
      const plaintext = await wrapWasm(
        'decrypt_group_message',
        {
          sender: senderAccountId,
          sender_device_id: senderDeviceId,
          ct_bytes: ciphertext.length,
          path: 'cold_chain_retry',
        },
        () =>
          client.decrypt_group_message(
            senderAccountId,
            senderDeviceId,
            ciphertext,
          ),
      );
      return {
        ...(JSON.parse(utf8Decode(plaintext)) as PlaintextPayload),
        iteration,
      };
    } catch (decryptError) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '[E2EE][decrypt-group] FAILED sender=',
          senderAccountId,
          '— both direct decrypt and cold-chain reset failed',
          'error=',
          decryptError,
        );
      }
      throw decryptError;
    }
  }

  /**
   * Generate SKDMs for all members of a group, encrypting each one via
   * the pairwise 1:1 Signal session to the recipient. This ensures the
   * server never sees plaintext SKDMs (R16 mitigation).
   *
   * H-3: if any member device's server-served identity key DIFFERS from the
   * stored one, the whole distribution throws an E2eeError with
   * `context.code === 'identity_key_changed'` BEFORE any session is archived
   * or re-pinned (old sessions stay intact; nothing is uploaded). Pass
   * `opts.allowIdentityKeyChange` only after explicit user acknowledgment of
   * the change.
   */
  async distributeKeysToMembers(
    distributionId: string,
    memberAccountIds: string[],
    opts?: IdentityChangeOpts,
  ): Promise<SenderKeyDistributionEntry[]> {
    const myId = this.myAccountId ?? 'unknown';
    // Other group members (not the sender)
    const recipients = memberAccountIds.filter((id) => id !== myId);
    let client = await this._getClient();

    // Establish 1:1 sessions with all recipients BEFORE calling
    // create_sender_key_distribution. The WASM SignalClient cannot
    // process new pre-key bundles after that call modifies its internal state.
    const readyRecipients: Array<{ accountId: string; deviceId: number }> = [];
    for (const recipientAccountId of recipients) {
      try {
        // Enumerate ALL of the recipient's devices (non-destructive). An SKDM is
        // a pairwise ciphertext bound to one device's session, so each device
        // needs its own — establishing only device 1 left every member's second
        // device unable to decrypt the group (multi-device GC bug).
        const identityDevices =
          await this.fetchIdentityOnly(recipientAccountId);
        if (identityDevices.length === 0) continue;

        let fullDevices: PublicDeviceBundle[] | null = null;
        for (const idev of identityDevices) {
          const deviceId = idev.device_id;
          const existing = await client.has_session(
            recipientAccountId,
            deviceId,
          );
          const storedIdKey = await this.store.loadRemoteIdentityKey(
            recipientAccountId,
            deviceId,
          );
          const idKeyChanged =
            !!storedIdKey && storedIdKey !== idev.identity_key;
          // H-3: never silently archive + re-establish onto a changed identity
          // key — that encrypts the group sender key to a potential MITM.
          // Abort the WHOLE distribution (nothing is uploaded; existing
          // sessions stay intact) so the caller can surface the change and
          // re-drive with a per-target allowance after user acknowledgment.
          const allowChange =
            !!opts?.allowIdentityKeyChange ||
            (opts?.allowIdentityKeyChangeFor ?? []).some(
              (a) =>
                a.accountId === recipientAccountId &&
                a.deviceId === deviceId &&
                (a.identityKey === undefined ||
                  a.identityKey === idev.identity_key),
            );
          if (idKeyChanged && !allowChange) {
            throw new E2eeError(
              'create_sender_key_distribution_message',
              {
                code: 'identity_key_changed',
                accountId: recipientAccountId,
                deviceId,
              },
              new Error(
                `remote identity key changed for ${recipientAccountId}:${deviceId} — SKDM distribution blocked pending user acknowledgment`,
              ),
            );
          }
          // An acked (allowChange) device with an existing session must be
          // re-established even though the ack already updated the stored pin
          // (idKeyChanged false) — otherwise the stale session to the OLD key
          // keeps being used for the SKDM.
          const needsEstablish =
            idKeyChanged || !existing || (allowChange && existing);
          if (needsEstablish) {
            if (!fullDevices) {
              // eslint-disable-next-line no-restricted-syntax -- PERMITTED: fetchDeviceList only when at least one device needs establishing
              fullDevices = await this.fetchDeviceList(recipientAccountId);
            }
            const device = fullDevices.find((d) => d.device_id === deviceId);
            if (!device) continue;
            if (existing && (idKeyChanged || allowChange)) {
              // Same acknowledged-re-key wedge as encryptToDevice: archive
              // alone leaves the WASM identity pin in place and the fresh
              // process_pre_key_bundle is refused. Remove + rebuild.
              try {
                await client.archive_session(recipientAccountId, deviceId);
              } catch {
                /* no-op */
              }
              try {
                // eslint-disable-next-line no-restricted-syntax -- PERMITTED: removeSession in acknowledged identity-key-change path, immediately before establishing onto the acked key
                await this.store.removeSession(recipientAccountId, deviceId);
              } catch {
                /* best-effort */
              }
              this.client = undefined;
              this.store.clearMemCache();
              client = await this._getClient();
            }
            await this.establishSession(
              client,
              recipientAccountId,
              deviceId,
              device,
            );
          }
          readyRecipients.push({ accountId: recipientAccountId, deviceId });
        }
      } catch (err) {
        // H-3: an unacknowledged identity-key change aborts the distribution —
        // do not swallow it into a per-recipient skip.
        if (
          err instanceof E2eeError &&
          err.context.code === 'identity_key_changed'
        ) {
          throw err;
        }
        console.warn(
          `[E2EE] SKDM session pre-establishment for ${recipientAccountId} failed, skipping:`,
          err,
        );
      }
    }

    // Also distribute to the sender's own secondary devices (multi-device support).
    // The sender's primary device (myDeviceId) is excluded; secondary devices
    // need the SKDM to decrypt group messages on those devices.
    if (this.myAccountId && memberAccountIds.includes(this.myAccountId)) {
      try {
        // Use fetchIdentityOnly to list own secondary devices without burning prekeys.
        // Only call fetchDeviceList when a session actually needs establishing.
        const myIdentityDevices = await this.fetchIdentityOnly(
          this.myAccountId,
        );
        const myOtherDeviceInfos = myIdentityDevices.filter(
          (d) => d.device_id !== this.myDeviceId,
        );
        let myFullDevices: PublicDeviceBundle[] | null = null;
        for (const deviceInfo of myOtherDeviceInfos) {
          try {
            const existing = await client.has_session(
              this.myAccountId,
              deviceInfo.device_id,
            );
            if (!existing) {
              if (!myFullDevices) {
                // eslint-disable-next-line no-restricted-syntax -- PERMITTED: fetchDeviceList only when own secondary device has no session
                myFullDevices = await this.fetchDeviceList(this.myAccountId);
              }
              const device = myFullDevices.find(
                (d) => d.device_id === deviceInfo.device_id,
              );
              if (!device) continue;
              await this.establishSession(
                client,
                this.myAccountId,
                device.device_id,
                device,
              );
            }
            readyRecipients.push({
              accountId: this.myAccountId,
              deviceId: deviceInfo.device_id,
            });
          } catch (devErr) {
            console.warn(
              `[E2EE] SKDM session pre-establishment for own device ${deviceInfo.device_id} failed, skipping:`,
              devErr,
            );
          }
        }
      } catch (err) {
        console.warn(
          '[E2EE] Failed to fetch own device list for SKDM distribution:',
          err,
        );
      }
    }

    const skdmB64 = await this.createSenderKeyDistribution(distributionId);
    if (process.env.NODE_ENV !== 'production') {
      // Diagnostic: check if any received sender keys survived create_sender_key_distribution
      console.debug(
        '[E2EE][distribute] createSenderKeyDistribution done for distId=',
        distributionId,
        '— checking received key state...',
      );
    }
    const out: SenderKeyDistributionEntry[] = [];

    for (const { accountId: recipientAccountId, deviceId } of readyRecipients) {
      try {
        // Session already established above; encrypt directly without re-fetching the bundle.
        // We store "type:ciphertext" so the receiver knows which Signal message type to use
        // for decryption (type 3 = PreKeySignalMessage, type 2 = WhisperMessage).
        const plaintext = utf8Encode(
          JSON.stringify({ text: `__skdm__:${skdmB64}` }),
        );
        const wasmCt = await client.encrypt_message(
          recipientAccountId,
          deviceId,
          plaintext,
        );
        try {
          await this.store.touchSessionTimestamp(
            recipientAccountId,
            deviceId,
            'send',
          );
          const sessionBytes = await client.export_session(
            recipientAccountId,
            deviceId,
          );
          if (sessionBytes) {
            await this.store.saveSession(
              recipientAccountId,
              deviceId,
              sessionBytes,
            );
          }
          out.push({
            recipientAccountId,
            recipientDeviceId: deviceId,
            distributionMessageB64: `${wasmCt.message_type}:${b64Encode(wasmCt.body)}`,
          });
        } finally {
          wasmCt.free();
        }
      } catch (err) {
        // Skip recipients we can't encrypt to rather than falling back to
        // plaintext. A plaintext SKDM would let the server read our group
        // sender key, breaking E2EE for the entire group (N01 fix).
        console.warn(
          `[E2EE] SKDM encryption to ${recipientAccountId} failed, skipping:`,
          err,
        );
      }
    }

    return out;
  }

  /**
   * Fetch and process all pending SKDMs from the server for a group.
   * SKDMs are encrypted via 1:1 Signal sessions, so we decrypt each
   * one before processing. Persists learned sender keys to IndexedDB.
   * Called automatically when opening a group chat.
   */
  async fetchAndProcessSenderKeys(groupChatId: string): Promise<boolean> {
    return this.withWriterLock(() =>
      this.fetchAndProcessSenderKeysLocked(groupChatId),
    );
  }

  /**
   * Returns true if at least one SKDM from a sender+device we had NOT previously
   * stored was processed this call (a genuinely new sender device — e.g. a member
   * device linked mid-conversation, or a re-key). Callers use this as a cheap,
   * bounded signal to reciprocate: re-distribute their own sender key so the new
   * device can decrypt their future messages. Gated on !alreadyProcessed so a
   * steady-state re-distribution of an already-known key never re-triggers it
   * (loop-safe).
   */
  private async fetchAndProcessSenderKeysLocked(
    groupChatId: string,
  ): Promise<boolean> {
    let sawNewSenderDevice = false;
    // Fetch group info and SKDMs; let API errors propagate (caller decides
    // whether to swallow or surface them — N10 fix).
    interface GroupInfo {
      distribution_id: string;
    }
    let distributionId: string;
    let skdms: ServerSKDM[];
    try {
      const groupInfo = await this.api<GroupInfo>(
        'GET',
        `v1/group_chats/${groupChatId}`,
      );
      distributionId = groupInfo.distribution_id;
      skdms = await this.api<ServerSKDM[]>(
        'GET',
        `v1/group_chats/${groupChatId}/sender_keys`,
        // Fetch only the SKDMs addressed to THIS device — each is a pairwise
        // ciphertext bound to one device's session.
        { params: { device_id: String(this.myDeviceId ?? 1) } },
      );
    } catch (err) {
      console.warn('[E2EE] Failed to fetch sender keys from server:', err);
      return sawNewSenderDevice;
    }

    // M-6b: a NEW distribution_id for this group means the epoch rotated
    // (member removed/left). Purge the OLD epoch's material — own + received
    // sender keys, stored SKDM bytes, iteration-0 init bytes, and the SKDM
    // envelope dedupe records — so a removed member's retained chain seed can
    // never be resurrected (removal-rollback) and old-epoch key material does
    // not accumulate in IDB. Only after a SUCCESSFUL group-info fetch; a fetch
    // failure purges nothing.
    const distKey = `group-dist:${groupChatId}`;
    const prevDistId = await this.store.load<string>(distKey);
    if (prevDistId && prevDistId !== distributionId) {
      await this.purgeGroupEpochLocked(prevDistId);
    }
    if (prevDistId !== distributionId) {
      await this.store.save(distKey, distributionId);
    }

    const client = await this._getClient();

    for (const skdm of skdms) {
      // Each SKDM is processed independently — one bad entry must not block
      // the rest (N10 + N19 fix).
      try {
        // M-6/M-8: the re-processing skip must be a CONTENT comparison, not a
        // boolean hasSkdmBytes short-circuit. The server re-serves the same
        // SKDM row on every poll; the 1:1 Signal envelope is single-use —
        // re-decrypting an identical replay would fail (and, for type 2,
        // needlessly archive the session). But a DIFFERENT envelope for a
        // known sender+distId is a REKEY whose new SKDM content must never be
        // dropped (the old boolean skip re-processed the OLD stored bytes and
        // silently discarded the new ones). The raw envelope string recorded
        // at last processing is what tells a true replay from a rekey.
        const storedBytes = await this.store.loadSkdmBytes(
          skdm.sender_account_id,
          skdm.sender_device_id,
          distributionId,
        );
        const alreadyProcessed = !!storedBytes && storedBytes.length > 0;
        const envelopeKey = `skdm-env:${skdm.sender_account_id}:${skdm.sender_device_id}:${distributionId}`;
        const storedEnvelope = await this.store.load<string>(envelopeKey);
        // Peek at the message type before deciding to skip. A PreKeySignalMessage
        // (type 3) means the sender re-established their 1:1 session — e.g. after
        // a key reset via the E2EE gate. Their stored SKDM bytes are from the old
        // sender key chain and must be replaced, so fall through to the normal
        // decrypt+store path even when alreadyProcessed=true.
        const peekColonIdx = skdm.distribution_message.indexOf(':');
        const peekMsgType =
          peekColonIdx > 0 && peekColonIdx <= 2
            ? parseInt(skdm.distribution_message.slice(0, peekColonIdx), 10)
            : 3;
        // Treat any non-2/3 value (including NaN from a malformed prefix) as
        // type 3 so a server-crafted bad prefix can't permanently suppress SKDM
        // updates for a known sender.
        const effectivePeekType =
          peekMsgType === 2 || peekMsgType === 3 ? peekMsgType : 3;
        if (process.env.NODE_ENV !== 'production') {
          console.debug(
            '[E2EE][SKDM] from',
            skdm.sender_account_id,
            'distributionId',
            distributionId,
            'alreadyProcessed:',
            alreadyProcessed,
            'peekMsgType:',
            peekMsgType,
            'effectivePeekType:',
            effectivePeekType,
          );
        }
        if (
          alreadyProcessed &&
          effectivePeekType !== 3 &&
          (storedEnvelope === skdm.distribution_message ||
            // Pre-migration entry (processed before envelope tracking existed):
            // cannot distinguish a re-served row from a rekey without burning
            // the single-use envelope — keep the old replay behavior and
            // migrate the record so future polls take the cheap path.
            storedEnvelope === undefined)
        ) {
          // TRUE REPLAY of an already-processed SKDM (identical envelope):
          // re-process stored bytes to reset the WASM receive chain. The chain
          // advances with each decrypt; on component re-mount within the same
          // page session restoreFromStore only runs once, so the chain is
          // already past the first message's iteration.
          if (storedEnvelope === undefined) {
            await this.store.save(envelopeKey, skdm.distribution_message);
          }
          if (storedBytes) {
            try {
              if (process.env.NODE_ENV !== 'production') {
                const iter = parseSignalProtobufVarint(storedBytes, 2);
                console.debug(
                  '[E2EE][SKDM] re-processing stored bytes to reset receive chain',
                  'sender=',
                  skdm.sender_account_id,
                  'distId=',
                  distributionId,
                  'skdmIteration=',
                  iter,
                );
              }
              await client.process_sender_key_distribution(
                skdm.sender_account_id,
                skdm.sender_device_id,
                storedBytes,
              );
            } catch (resetErr) {
              console.warn(
                '[E2EE] Failed to reset receive chain for',
                skdm.sender_account_id,
                ':',
                resetErr,
              );
            }
          }
          continue;
        }

        let skdmBytes: Uint8Array;

        // The distribution_message is stored as "type:ciphertext" where
        // type is the Signal message type (2=WhisperMessage, 3=PreKey).
        const colonIdx = skdm.distribution_message.indexOf(':');
        let msgType: number;
        let ctB64: string;

        if (colonIdx > 0 && colonIdx <= 2) {
          msgType = parseInt(skdm.distribution_message.slice(0, colonIdx), 10);
          ctB64 = skdm.distribution_message.slice(colonIdx + 1);
        } else {
          msgType = 3;
          ctB64 = skdm.distribution_message;
        }

        if (process.env.NODE_ENV !== 'production') {
          console.debug(
            '[E2EE][SKDM] processing from',
            skdm.sender_account_id,
            'msgType=',
            msgType,
            'ctB64 len=',
            ctB64.length,
            'raw prefix=',
            skdm.distribution_message.slice(0, 10),
          );
        }

        // skdmBytes starts null; if decryption fails or format is unexpected we
        // skip this SKDM rather than falling back to raw server bytes (R16).
        let skdmBytesOrNull: Uint8Array | null = null;
        try {
          // Call the *Locked variant: we already hold the writer lock
          // (fetchAndProcessSenderKeys is wrapped). Re-entering withWriterLock
          // would deadlock the non-reentrant Web Lock.
          const payload = await this.decryptMessageLocked(
            skdm.sender_account_id,
            ctB64,
            msgType,
            skdm.sender_device_id,
          );
          if (payload.text?.startsWith('__skdm__:')) {
            skdmBytesOrNull = b64Decode(payload.text.slice('__skdm__:'.length));
          } else {
            // Decryption succeeded but payload has no __skdm__ prefix.
            // Post-N01, all valid SKDMs have this prefix — skip rather than
            // processing raw server bytes, which would partially revert R16.
            console.warn(
              '[E2EE][SKDM] unexpected payload format from',
              skdm.sender_account_id,
              '— skipping (missing __skdm__ prefix)',
            );
          }
          if (process.env.NODE_ENV !== 'production') {
            console.debug(
              '[E2EE][SKDM] decryptMessage succeeded, payload prefix=',
              payload.text?.slice(0, 20),
            );
          }
        } catch (decryptErr) {
          // Post-N01, all SKDMs are encrypted via pairwise Signal sessions.
          // A decryption failure means a corrupt or unauthorized entry — skip it.
          // Never fall back to raw server bytes (that would partially revert R16).
          // For WhisperMessage failures, archive the stale session so the sender's
          // next distribution attempt produces a PreKeyMessage and self-heals.
          if (msgType === 2) {
            try {
              await client.archive_session(
                skdm.sender_account_id,
                skdm.sender_device_id,
              );
              // LOW-9 (resolved BY-DESIGN): deliberately NOT persisted — see the
              // matching note in decryptMessageLocked. Persisting an archived
              // session bricks the send path on rebuild.
            } catch {
              /* best-effort */
            }
          }
          console.warn(
            '[E2EE][SKDM] failed to decrypt SKDM from',
            skdm.sender_account_id,
            '— skipping:',
            decryptErr,
          );
        }

        if (!skdmBytesOrNull) continue;
        skdmBytes = skdmBytesOrNull;

        // M-6: CONTENT comparison against the stored SKDM bytes. Identical
        // content = true replay (e.g. a type-3 session re-establishment
        // re-wrapping the same sender key) — it is still processed below
        // (required for session re-establishment) but nothing material
        // changes. DIFFERENT content for an already-known sender+distId = a
        // REKEY: the new sender key MUST be adopted (the old boolean skip
        // dropped it, leaving every member on the old receive chain).
        if (
          alreadyProcessed &&
          storedBytes &&
          !bytesEqual(storedBytes, skdmBytes)
        ) {
          console.warn(
            '[E2EE][SKDM] rekey detected — adopting new sender key for',
            skdm.sender_account_id,
            'distId=',
            distributionId,
          );
        }

        if (process.env.NODE_ENV !== 'production') {
          const skdmIter = parseSignalProtobufVarint(skdmBytes, 3);
          console.debug(
            '[E2EE][SKDM] calling process_sender_key_distribution',
            'sender=',
            skdm.sender_account_id,
            'distId=',
            distributionId,
            'len=',
            skdmBytes.length,
            'skdmIteration=',
            skdmIter,
            '← receiver chain will start at this iteration',
            'hex=',
            hexDump(skdmBytes, 16),
          );
        }
        await client.process_sender_key_distribution(
          skdm.sender_account_id,
          skdm.sender_device_id,
          skdmBytes,
        );

        if (process.env.NODE_ENV !== 'production') {
          try {
            const verifyKey = await client.export_sender_key(
              skdm.sender_account_id,
              skdm.sender_device_id,
              distributionId,
            );
            console.debug(
              '[E2EE][SKDM] export_sender_key after process:',
              verifyKey
                ? `${verifyKey.length} bytes ✓`
                : 'NULL ← distId mismatch?',
              'sender=',
              skdm.sender_account_id,
              'groupDistId=',
              distributionId,
            );
          } catch (exportErr) {
            console.debug('[E2EE][SKDM] export_sender_key threw:', exportErr);
          }
        }

        // Persist raw SKDM bytes so restoreFromStore can re-call
        // process_sender_key_distribution on future page loads. This is the
        // correct persistence mechanism for received sender keys — export_sender_key
        // only works for own keys and import_sender_key does not populate the
        // WASM decrypt store that decrypt_group_message reads from.
        await this.store.saveSkdmBytes(
          skdm.sender_account_id,
          skdm.sender_device_id,
          distributionId,
          skdmBytes,
        );
        // M-6: record the raw envelope that produced these bytes so the next
        // poll can recognize an identical replay without re-decrypting it.
        await this.store.save(envelopeKey, skdm.distribution_message);
        // A first-time SKDM from this sender+device: signal the caller to
        // reciprocate (re-distribute our own sender key to this new device).
        // Gated on !alreadyProcessed so steady-state re-distributions of an
        // already-known key never re-trigger reciprocation (loop-safe).
        if (!alreadyProcessed) sawNewSenderDevice = true;
        if (process.env.NODE_ENV !== 'production') {
          console.debug(
            '[E2EE][SKDM] saved raw bytes for',
            skdm.sender_account_id,
            'distributionId',
            distributionId,
          );
        }
      } catch (skdmErr) {
        console.warn(
          '[E2EE] Failed to process SKDM from',
          skdm.sender_account_id,
          ':',
          skdmErr,
        );
      }
    }
    return sawNewSenderDevice;
  }

  /**
   * Upload our SKDM for the given group to the server, distributing
   * to all specified member account IDs.
   *
   * H-3: throws an E2eeError with `context.code === 'identity_key_changed'`
   * (nothing uploaded, old sessions intact) when a member's server-served
   * identity key differs from the stored one. Pass
   * `opts.allowIdentityKeyChange` only after explicit user acknowledgment.
   */
  async uploadSenderKeyDistribution(
    groupChatId: string,
    distributionId: string,
    memberAccountIds: string[],
    opts?: IdentityChangeOpts,
  ): Promise<number> {
    return this.withWriterLock(() =>
      this.uploadSenderKeyDistributionLocked(
        groupChatId,
        distributionId,
        memberAccountIds,
        opts,
      ),
    );
  }

  private async uploadSenderKeyDistributionLocked(
    groupChatId: string,
    distributionId: string,
    memberAccountIds: string[],
    opts?: IdentityChangeOpts,
  ): Promise<number> {
    const entries = await this.distributeKeysToMembers(
      distributionId,
      memberAccountIds,
      opts,
    );
    if (entries.length === 0) return 0;

    await this.api('POST', `v1/group_chats/${groupChatId}/distribute_keys`, {
      data: {
        distribution_id: distributionId,
        sender_device_id: this.myDeviceId ?? 1,
        distributions: entries.map((e) => ({
          recipient_account_id: e.recipientAccountId,
          recipient_device_id: e.recipientDeviceId,
          distribution_message: e.distributionMessageB64,
        })),
      },
    });
    return entries.length;
  }

  /**
   * M-6b: purge ALL local key material for an OLD group epoch (distribution
   * id): own + received sender keys, stored SKDM bytes, iteration-0 init
   * bytes, and SKDM envelope dedupe records. Called automatically by
   * fetchAndProcessSenderKeys when the server's distribution_id for a group
   * changes; also exposed for the rotation flow (actions/group_chats.ts) to
   * call after successfully creating+distributing under a new distId.
   */
  async purgeGroupEpoch(distributionId: string): Promise<void> {
    return this.withWriterLock(() =>
      this.purgeGroupEpochLocked(distributionId),
    );
  }

  private async purgeGroupEpochLocked(distributionId: string): Promise<void> {
    const accountId = this.myAccountId ?? 'unknown';
    const deviceId = this.myDeviceId ?? 1;
    // Remove our old sender key for this distribution
    await this.store.removeSenderKey(accountId, deviceId, distributionId);
    // Also purge ALL sender keys for this distribution (from any sender) so
    // stale keys from removed members don't accumulate in IDB (N27 fix).
    await this.store.removeSenderKeysForGroup(distributionId);
    await this.store.removeSkdmBytesForGroup(distributionId);
    // Clear stored initial bytes so the next distribution creates fresh ones
    // from the post-rekey WASM chain state (not the pre-rekey iteration-0 bytes).
    await this.store.removeInitialSkdmBytesForGroup(distributionId);
    // M-6: purge the raw-envelope dedupe records for this epoch too, so no
    // old-epoch residue survives the purge.
    const dump = await this.store.dump();
    for (const key of Object.keys(dump)) {
      if (key.startsWith('skdm-env:') && key.endsWith(`:${distributionId}`)) {
        await this.store.remove(key);
      }
    }
  }

  /**
   * Re-key: generate a fresh Sender Key for a group (e.g. after a
   * member is removed). Old sender key is discarded; a new SKDM is
   * distributed to remaining members.
   *
   * H-3: like uploadSenderKeyDistribution, throws an E2eeError with
   * `context.code === 'identity_key_changed'` on an unacknowledged key change;
   * pass `opts.allowIdentityKeyChange` only after explicit user acknowledgment.
   */
  async rekeyGroup(
    groupChatId: string,
    distributionId: string,
    remainingMemberAccountIds: string[],
    opts?: IdentityChangeOpts,
  ): Promise<void> {
    return this.withWriterLock(() =>
      this.rekeyGroupLocked(
        groupChatId,
        distributionId,
        remainingMemberAccountIds,
        opts,
      ),
    );
  }

  private async rekeyGroupLocked(
    groupChatId: string,
    distributionId: string,
    remainingMemberAccountIds: string[],
    opts?: IdentityChangeOpts,
  ): Promise<void> {
    await this.purgeGroupEpochLocked(distributionId);
    // Create fresh and distribute. Call the *Locked variant: rekeyGroup already
    // holds the writer lock (re-entering it would deadlock the non-reentrant
    // Web Lock).
    await this.uploadSenderKeyDistributionLocked(
      groupChatId,
      distributionId,
      remainingMemberAccountIds,
      opts,
    );
  }
}

export { signalInitSync as initSync };
